import { useState, useEffect, useRef, useMemo } from "react";
import { withTranslation } from "react-i18next";
import {blue, lightBlue, yellow} from "@mui/material/colors";
import {cloneDeep, isEqual, isUndefined, size, isBoolean, isEmpty} from "lodash";
import { FilterAlt, Star} from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { Box, FormControl, InputLabel, MenuItem, OutlinedInput, Select, Tooltip, TextField, Autocomplete, IconButton, Checkbox } from "@mui/material";
import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";
import HrchyLevelSegments from "@plannel/components/ui/HrchyLevelSegments";
import { AgGridReact } from "ag-grid-react";
import { addMonths, subDays } from "date-fns";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";

import { FilterButton, SaveButton, SettingsButton } from "@plannel/components/ActionIconButton";
import * as XLSX from "xlsx";
import NumericEditor from "@plannel/components/aggrid/NumericEditor";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import DataState from "@plannel/components/aggrid/DataState";
import AbcXyzChip from "@plannel/components/aggrid/AbcXyzChip";
import hrchyConfigService from "@plannel/services/data/hrchy-config-service";
import npiHistoryService from "@plannel/services/dp/dp-npi-history-service";
import companyService from "@plannel/services/data/company-service";
import itemService from "@plannel/services/data/item-service";
import planConfigService from "@plannel/services/data/plan-config-service";
import planVersionService from "@plannel/services/data/plan-version-service";
import measureService from "@plannel/services/data/measure-service";
import demandValService from "@plannel/services/data/demand-val-service";
import CustomDetailHeader from "@plannel/components/aggrid/CustomDetailHeader"
import codeUtil from "@plannel/pages/utils/code-util";
import dateUtils from "@plannel/utils/date-util";
import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import Snackbar from "@plannel/components/Snackbar";
import Dialog from "@plannel/components/Dialog";
import customerItemService from "@plannel/services/data/customer-item-service";
import {DPFilterModal} from "@plannel/pages/demand-plan/dp-workbench/dp-workbench-modal/FilterModal";
import CustomerHrchyTreeFilter from "@plannel/components/filter/CustomerHrchyTreeFilter";
import AbcXyzFilterModal from "@plannel/components/modal/AbcXyzFilterModal";
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ColorUtil from "@plannel/pages/utils/color-util";
import ChartComposed from "@plannel/components/rechart/ChartComposed";
import ExcelExportButton from "@plannel/components/ExcelExportButton";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";
import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
import GridDatePicker from "@plannel/components/GridDatePicker";
import SplitterContainer from "@plannel/components/SplitterContainer";
import { FS } from "@plannel/utils/font-util";
const { snackBarMessageColor: { saveSuccess, saveFail } } = ColorUtil;

const ITEM_WEIGHT_RATE_GRID = "ITEM_WEIGHT_RATE_GRID";
const BUCKET_ITEM_GRID = "BUCKET_ITEM_GRID";
const ITEM_GRID = "ITEM_GRID";
const ITEM_HRCHY = "itemHrchy";

const NpiHistory = ({ t, viewName, title }) => {
  const reduxViewState = cloneDeep(reduxUtil.getViewState(viewName));
  const reduxDispatch = useDispatch();
  const reduxState = reduxUtil.createObjectState(viewName);
  reduxDispatch(reduxUtil.updateViewState({ ...reduxState }));
  const isRedux = useRef(!isEmpty(reduxViewState?.npiCustomer));
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [selectAll, setSelectAll] = useState(false);
  const CURRENT_DATE = addMonths(new ZDate(), -1);
  const START_DATE = addMonths(CURRENT_DATE, -12);
  const PLAN_FALLBACK_START_DATE = new ZDate();
  const PLAN_FALLBACK_END_DATE = addMonths(PLAN_FALLBACK_START_DATE, 12);

  const { muicolor, buckets, isWeekBucket, dpSettingCode, filterType, customer, item } = codeUtil;
  const { DP } = codeUtil.module;
  const { BUCKET_UNIT, LEVEL_CONFIG } = codeUtil.dpConfig;
  const { snackBarMessageColor } = ColorUtil;
  const { ITEM_LEVEL, CUST_LEVEL } = dpSettingCode;
  const { SCENARIO, RELEASED } = codeUtil.versionStatusCode;
  const { MF_QTY } = codeUtil.dpMeasure;

  const [startDate, setStartDate] = useState(reduxViewState?.startDate ?? dateUtils.startOfMonthDate(START_DATE));
  const [endDate, setEndDate] = useState(reduxViewState?.endDate ?? dateUtils.endOfMonthDate(CURRENT_DATE));
  const [npiList, setNpiList] = useState(reduxViewState?.npiList ?? []);

  const [selectedNpis, setSelectedNpis] = useState(reduxViewState?.selectedNpis ?? [])
  const [customConfig, setCustomConfig] = useState({});
  const [npiCustomer, setNpiCustomer] = useState(reduxViewState?.npiCustomer ?? {});
  const [isLoad, setIsLoad] = useState(false);
  const [customerList, setCustomerList] = useState(reduxViewState?.customerList ?? []);
  const [isActualLoad, setIsActualLoad] = useState(false);
  const actions = ['sum', 'avg'];
  const [action, setAction] = useState(reduxViewState?.action ?? actions[0]);
  const applyTypes = ['ACTUAL', 'PLAN'];
  const [applyType, setApplyType] = useState(reduxViewState?.applyType ?? applyTypes[0]);
  const isPlanApply = applyType === 'PLAN';

  const [dpScenarioVersion, setDpScenarioVersion] = useState(null);
  const [mfMeasure, setMfMeasure] = useState(null);
  const [dpScenarioVersions, setDpScenarioVersions] = useState([]);
  const [hasScenarioVersion, setHasScenarioVersion] = useState(false);
  const [updateVersionData, setUpdateVersionData] = useState(true);
  const [truncateBeforeImport, setTruncateBeforeImport] = useState(false);
  const applyTypeMounted = useRef(false);
  const levelConfigMounted = useRef(false);
  const appliedFilterListKey = useRef(null);
  const planValueLoading = useRef(false);
  const availableApplyTypes = dpScenarioVersion ? applyTypes : [applyTypes[0]];
  const [groupBy, setGroupBy] = useState(reduxViewState?.groupBy ?? ITEM_HRCHY);
  const [attrColDefs, setAttrColDefs] = useState([]);
  const [hrchyColDefs, setHrchyColDefs] = useState([]);
  const [invClassCdDefs, setInvClassCdDefs] = useState([]);
  const [bucketUnit, setBucketUnit] = useState(buckets.MONTH);

  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [levelConfig, setLevelConfig] = useState(reduxViewState?.levelConfig ?? {});
  /*
    selected like profile grid
   */
  const gridRef = useRef();
  const selectedItemsFileInputRef = useRef();
  const [selectedItemsGridApi, setSelectedItemsGridApi] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [originNpiProfile, setOriginNpiProfile] = useState([]);
  /*
    npi grid
  */
  const [npiItemsGridApi, setNpiItemsGridApi] = useState(null);
  const originHistoryList = useRef([]);  //cell style 비교용
  const npiHistoryList = useRef();
  const isFilterChanged = useRef(false);  //startDate/endDate 필터 변경으로 인한 retrieveItemsActual 호출인지 구분
  const npiHistoryLoadingRef = useRef(false);  //retrieveNpiHistory(및 위임 대상 retrieveNpiPlanValue) 진행 중에는 재호출을 막는다

  /*
    target of like profile grid
  */
  // const [itemsGridApi, setItemsGridApi] = useState(null);
  const [customerItems, setCustomerItems] = useState([]);
  const weekFormatHeader = useRef();
  const [gridHeader, setGridHeader] = useState([]);
  const [npiHeaderObj, setNpiHeaderObj] = useState({});
  const [gridData, setGridData] = useState([]);

  /*cache*/
  const npiTxnCacheGridData = useRef({});
  const npiTxnCacheData = useRef({});
  const npiTxnCacheColDef = useRef([]);
  const npiTxnCache = useRef([]);
  /*
    chart
   */
  const [chartData, setChartData] = useState([]);
  const [chartSeriesKeys, setChartSeriesKeys] = useState([]); // 번역 전 측정 키 목록

  /*
   * 시리즈의 groupName 을 조회 시점에 t() 로 굳히면 언어를 바꿔도 옛 언어가 남는다.
   * 렌더 시점에 t 와 함께 다시 만든다. (field/text 는 서버 측정 키라 번역 대상이 아니다.)
   */
  const chartSeries = useMemo(() => chartSeriesKeys.map(key => ({
    field: key, text: key, type: codeUtil.toggleType.BAR, typeOptions: { strokeWidth: 1 }, orientation: "left", groupName: t("qty"), backgroundColor: "#eee"
  })), [chartSeriesKeys, t]);
  /*
  autocomplete
   */
  const [settingModalOpen, setSettingModalOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [inputCustValue, setInputCustValue] = useState('');

  /* filter */
  const [reload, setReload] = useState(false);
  const [checkBoxReload, setCheckBoxReload] = useState(false);
  const [defaultValue] = useState([]);
  const [itemHrchyDefaultValue] = useState([]);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [custCustomValue, setCustCustomValue] = useState([]);
  const [itemCustomValue, setItemCustomValue] = useState([]);
  const [itemFilterList, setItemFilterList] = useState(reduxViewState?.itemFilterList ?? {});
  const [customerFilterList, setCustomerFilterList] = useState(reduxViewState?.customerFilterList ?? {});
  const [customerFilterColor, setCustomerFilterColor] = useState(size(reduxViewState?.customerFilterList) > 0 ? muicolor.INFO : muicolor.DEFAULT);
  const [itemFilterColor, setItemFilterColor] = useState(size(reduxViewState?.itemFilterList) > 0 ? muicolor.INFO : muicolor.DEFAULT);
  const [custFilterType, setCustFilterType] = useState(reduxViewState?.custFilterType ?? [filterType.or.cd, filterType.equal.cd]);
  const [itemFilterType, setItemFilterType] = useState(reduxViewState?.itemFilterType ?? [filterType.or.cd, filterType.equal.cd]);
  const [treeload, setTreeload] = useState(false);
  const [itemTreeLoad, setItemTreeLoad] = useState(false);
  const [modelCode, setModelCode] = useState(customer.name);
  const [filterData, setFilterData] = useState({});
  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? {});
  const [invClassCdList, setInvClassCdList] = useState(reduxViewState?.invClassCdList ?? []);
  const [disabledInvClass, setDisabledInvClass] = useState(false);

  /*modal*/
  const [itemLevelCds, setItemLevelCds] = useState(reduxViewState?.itemLevelCds ??[]);
  const [custLevelCds, setCustLevelCds] = useState(reduxViewState?.custLevelCds ??[]);
  const [modalCustLevelCd, setModalCustLevelCd] = useState({});
  const [modalItemLevelCd, setModalItemLevelCd] = useState({});
  const itemAttrFields = ["attr01", "attr02", "attr03", "attr04", "attr05", "attr06", "attr07", "attr08", "attr09", "attr10"];
  const custAttrFields = ["custAttr01", "custAttr02", "custAttr03", "custAttr04", "custAttr05", "custAttr06", "custAttr07", "custAttr08", "custAttr09", "custAttr10"];
  const itemAttrColsCollapsed = reduxViewState && Object.prototype.hasOwnProperty.call(reduxViewState, "itemAttrColsCollapsed") ? reduxViewState.itemAttrColsCollapsed : true;
  const custAttrColsCollapsed = reduxViewState && Object.prototype.hasOwnProperty.call(reduxViewState, "custAttrColsCollapsed") ? reduxViewState.custAttrColsCollapsed : true;

  /*advanced filter*/
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? muicolor.INFO : muicolor.DEFAULT);
  const [openFilter, setOpenFilter] = useState(false);

  /*combo advanced flter*/
  const [comboAdvancedFilters, setComboAdvancedFilters] = useState(reduxViewState?.comboAdvancedFilters ? reduxViewState.comboAdvancedFilters : null);
  const [comboAdvancedFilterColor, setComboAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.comboAdvancedFilters) ? muicolor.INFO : muicolor.DEFAULT);
  const [openComboFilter, setOpenComboFilter] = useState(false);
  const [comboFilterPanelWidth, setComboFilterPanelWidth] = useState(420);
  const [isComboFilterResizing, setIsComboFilterResizing] = useState(false);
  const comboFilterLayoutRef = useRef(null);

  const attrDef = (targetObject, attrFields, attrColsCollapsed, isItem) => {
    let attrLoop = 0;
    const columnDefs = [];
    for (const attrField of attrFields) {
      const index = itemAttrFields[attrLoop];
      const headerNameValue = targetObject?.[index] ?? t(attrField)

      if (isBoolean(attrColsCollapsed)) {
        const parentDetailColumns = [...attrFields];
        // shift 하는 이유는 자기자신(parent)는 제외하기 위함(전체 attr 컬럼을 deep copy후 parent에 부여).
        parentDetailColumns.shift();

        const attrParentOption = {
          width: 120,
          headerComponent: CustomDetailHeader,
          headerComponentParams: {
            viewName,
            colsHide: attrColsCollapsed, reduxKey: isItem ? "itemAttrColsCollapsed" : "custAttrColsCollapsed",
            detailColumns: parentDetailColumns,
          },
        };
        // attrLoop: 0 = parent, child: other only hide option.
        if (attrLoop === 0) {
          columnDefs.push({ headerName: headerNameValue, field: attrField, filterType: "string", filterName: attrField, width: 100, ...attrParentOption });
        } else {
          columnDefs.push({ headerName: headerNameValue, field: attrField, filterType: "string", filterName: attrField, width: 100, hide: attrColsCollapsed });
        }
      } else {
        columnDefs.push({ headerName: headerNameValue, field: attrField, filterType: "string", filterName: attrField, width: 100, hide: false });
      }
      attrLoop++;
    }
    return columnDefs;
  }

  const advancedFilterColumnDefs = [
    { headerName: "itemCd", filterType: "string" },
    { headerName: "itemName", filterType: "string" },
    { headerName: "itemHrchyCd", filterType: "string" },
    { headerName: "itemHrchy", filterType: "string" },
    ...attrDef(customConfig?.item, itemAttrFields, true, true),
    { headerName: "endOfSalesDt", field: "endOfSalesDt", type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, width: 120, filterType: "date", valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd")},
    { headerName: "readyToSellinDt", field: "readyToSellinDt", type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, width: 120, filterType: "date", valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd")}
  ].filter(Boolean);

  const SPLITER = "#SPLIT#";
  const defaultItemWeightRateGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName, gridId: ITEM_WEIGHT_RATE_GRID });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const defaultBucketItemGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName, gridId: BUCKET_ITEM_GRID });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const defaultItemGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName, gridId: ITEM_GRID });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /*
   * 계획반영은 CUSTOMER-ITEM 조합만 지원한다.
   * DP 공통 설정(LEVEL_CONFIG) 저장값은 건드리지 않고, 화면이 참조하는 유효 레벨만 leaf 로 고정한다.
   */
  const effectiveLevel = isPlanApply
    ? { custLevelCd: customer.cd, itemLevelCd: item.cd }
    : (levelConfig?.objVal ?? {});

  const isCustomer = effectiveLevel?.custLevelCd === customer.cd;
  const isItem = effectiveLevel?.itemLevelCd === item.cd

  /*
   * 판매 개시일/단종일 — item 레벨에서만 서버가 채워 준다(계층 노드에는 해당 값이 없다).
   * 단종일은 z_customer_item 값이 없으면 z_item 값으로 대체된 값이다.
   */
  const lifecycleDtColDefs = isItem ? [
    { headerName: "readyToSellinDt", field: "readyToSellinDt", width: 120, editable: false, filterType: "date",
      valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd") },
    { headerName: "endOfSalesDt", field: "endOfSalesDt", width: 120, editable: false, filterType: "date",
      valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd") }
  ] : [];

  const editableHistory = (params) => {
    const itemCds = selectedNpis.map(item => item.itemCd);
    return itemCds.includes(params.itemCd);
  }

  const handleReset = () => {
    npiTxnCache.current = [];
    npiHistoryList.current = [];
    npiTxnCacheGridData.current = {};
    npiTxnCacheData.current = {}
    npiTxnCacheColDef.current = []
    setSelectedItems([])
    setGridData([]);
    setChartData([]);
    setChartSeriesKeys([]);
  }

  const dataCellStyle = (params, isSellinBucket = true) => {
    const cellStyle = {};
    if (params) {
      if (params.column.colId === "weightRate" && action === actions[0]) {
        if (action === actions[0]) {
          cellStyle.backgroundColor = lightBlue[100];
          if (params.data.modify && Number(params.data.weightRate) !== originNpiProfile[params.data.customerCd+params.data.itemCd]) {
            cellStyle.color = blue.A700;
            cellStyle.fontWeight = "bold";
          }
        }
      } else {
        // selectedNpis 배열의 모든 항목 확인
        const isSelectedNpi = selectedNpis.some(npiItem =>
          params.data.itemCd === npiItem.itemCd &&
          params.data.customerCd === npiCustomer.customerCd
        );

        // 신제품 행이라도 판매개시일 이전 버킷은 편집할 수 없어 색을 칠하지 않는다
        if (isSelectedNpi && isSellinBucket) {
          cellStyle.backgroundColor = lightBlue[100];
        }

        if (params.data.modify) {
          const origin = {}
          for (const data of originHistoryList.current) {
            const date = isWeekBucket(bucketUnit) ? dateUtils.getWeekString(new ZDate(data.baseDate)) : dateUtils.headerDateFormatter(new ZDate(data.baseDate), bucketUnit)
            origin[date] = data.qty;
          }
          if (origin[params.colDef.field] !== params.data[params.colDef.field]) {
            cellStyle.color = blue.A700;
            cellStyle.fontWeight = "bold";
          }
        }
      }
    }
    return cellStyle
  }

  const itemRenderer = (params) => {
    return (
      <Box>
        <span>{params.value}</span>
        <Star sx={{ ml: 0.5, width: "15px", height: "15px", color: yellow[800], verticalAlign: "text-bottom" }} />
      </Box>
    );
  }

  const setColumnDefs = (headers, timeDimension) => {
    const formatHeaderList = [];
    for (const index in headers) {
      if (Object.prototype.hasOwnProperty.call(headers, index)) {
        const current = headers[index];
        const temp = new ZDate(current);
        const date = new ZDate(temp.getFullYear(), temp.getMonth(), temp.getDate(), 0, 0, 0);
        let format = dateUtils.headerDateFormatter(date, timeDimension);
        if (isWeekBucket(timeDimension)) {
          format = weekFormatHeader.current[current];
        }
        formatHeaderList.push(format);
      }
    }
    const npiItemColumnDefs = [
      {
        headerName: isCustomer ? "customer" : "customerHrchy",
        field: "customer",
        chartDataType: "category",
        pinned: "left", lockPinned: true,
        cellClass: "stringType",
        width: 150,
        editable: false,
      },
      {
        headerName: isItem ? "item" : "itemHrchy",
        field: "item",
        chartDataType: "category",
        pinned: "left", lockPinned: true,
        cellClass: "stringType",
        width: 150,
        editable: false,
        cellRendererSelector: (params) => {
          const itemDetails = {
            component: itemRenderer,
          };
          if (params.data) {
            if (selectedNpis?.some(npi => npi.itemCd === params.data.itemCd) && params.data.customer === npiCustomer?.name) {
              return itemDetails;
            } else {
              return params.value;
            }
          }
          return undefined;
        },
      }
    ];

    for (const index in formatHeaderList) {
      if (Object.prototype.hasOwnProperty.call(formatHeaderList, index)) {
        const header = formatHeaderList[index];
        /*
         * 신제품은 판매개시일 이후 버킷만 편집 대상이다. 버킷 종료일(다음 버킷 시작일 -1일, 마지막은 조회 종료일)
         * 기준으로 판단해 개시일이 버킷 중간에 걸리면 그 버킷은 포함한다(자동계산 규칙과 동일).
         */
        const bucketIndex = Number(index);
        const nextBaseDate = headers[bucketIndex + 1];
        const bucketEnd = nextBaseDate ? subDays(new ZDate(nextBaseDate), 1) : new ZDate(endDate);
        const isSellinBucket = (data) => {
          // 판매개시일 제한은 계획반영(신품목 수요 맵핑) 전용 — 실적반영은 기존과 동일하게 전 구간 편집 가능하다
          if (!isPlanApply) {
            return true;
          }
          const npi = selectedNpis.find(item => isEqual(item.itemCd, data?.itemCd));
          if (!npi?.readyToSellinDt) {
            return true;
          }
          return bucketEnd >= new ZDate(npi.readyToSellinDt);
        };
        const isEditableBucket = (data) => {
          // 시나리오 버전이 없어 최신 릴리즈 버전으로 대체한 계획반영은 값을 고칠 수 없다(편집·파란 배경 없음)
          if (isPlanApply && !hasScenarioVersion) {
            return false;
          }
          return editableHistory(data) && isSellinBucket(data);
        };

        npiItemColumnDefs.push({
          headerName: header,
          field: header,
          width: 110,
          singleClickEdit: false,
          type: ["numberColumn", "rightAligned"],
          chartDataType: "series",
          cellClassRules: {
            editableBackgroud: ({ data }) => isEditableBucket(data),
          },
          cellStyle: params => dataCellStyle(params, isEditableBucket(params?.data)),
          cellEditor: NumericEditor,
          editable: ({ data }) => {
            return isEditableBucket(data);
          }
        });
      }
    }
    setNpiHeaderObj({ colDef: npiItemColumnDefs, gridHeaders: formatHeaderList, originHeaders: headers });
    return { colDef: npiItemColumnDefs, gridHeaders: formatHeaderList, originHeaders: headers };
  };

  const setGridDatas = (notExistItems, results, formatHeaderList) => {
    const datas = {};
    const resultMap = results ? results.reduce((acc, cur) => {
      const customerKey = isCustomer ? cur.customerCd : cur.customerHrchyCd;
      const itemKey = isItem ? cur.itemCd : cur.itemHrchyCd;
      const key = customerKey+itemKey;
      acc[key] = cur;
      return acc;
    }, {}) : {};

    for (const item of notExistItems) {
      const row = {
        customerCd: item.likeCustomerNodeCd,
        customer: item.likeCustomerNodeName,
        itemCd: item.likeItemNodeCd,
        item: item.likeItemNodeName
      }
      const key = row.customerCd+row.itemCd;
      if (!isEmpty(resultMap) && resultMap[key]) {
        const result = resultMap[key];
        for (let i = 0; i < formatHeaderList.length; i++) {
          const header = formatHeaderList[i];
          if (result.pivotData) {
            row[header] = result.pivotData[i];
          }
        }
      }
      datas[key] = row;
    }
    if (Object.keys(datas).length > 0) {
      return Object.values(datas);
    }
    return [];
  };

  const sortSelectedItems = (items) => {
    return items.sort((a, b) => {
      if (a.likeCustomerNodeCd !== b.likeCustomerNodeCd) {
        return a.likeCustomerNodeCd.localeCompare(b.likeCustomerNodeCd);
      }
      return a.likeItemNodeCd.localeCompare(b.likeItemNodeCd);
    });
  };

  const sortGridItems = (items) => {
    return items.sort((a, b) => {
      if (a.customerCd !== b.customerCd) {
        return a.customerCd.localeCompare(b.customerCd);
      }
      return a.itemCd.localeCompare(b.itemCd);
    });
  };

  const checkFormatter = params => {
    const checkActive = (event) => {
      params.node.data.useFlg = event?.target?.checked;
      if (npiCustomer == null || isEmpty(npiCustomer)) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoCustomer") });
        return;
      }
      if (params.node.data.useFlg) {
        const parameter = {
          customerNodeId: npiCustomer.id,
          customerNodeCd: npiCustomer.customerCd,
          customerNodeName: npiCustomer.name,
          likeItemNodeId: params.node.data?.itemNodeId,
          likeItemNodeCd: params.node.data?.itemNodeCd,
          likeItemNodeName: params.node.data?.itemNodeName,
          likeCustomerNodeId: params.node.data?.customerNodeId,
          likeCustomerNodeCd: params.node.data?.customerNodeCd,
          likeCustomerNodeName: params.node.data?.customerNodeName,
          activeFlg: params.node.data?.activeFlg,
          actionCd: action,
          weightRate: action === actions[0] ? 100 : null,
        }
        const newSelectedItems = [...selectedItems, parameter];
        if (action === "avg") {
          for (const item of newSelectedItems) {
            item.weightRate = 100 / newSelectedItems.length;
          }
        }
        setSelectedItems(sortSelectedItems(newSelectedItems));

      } else {
        const nodeData = params.node.data;
        const deleteItem = selectedItems.filter(el => el.likeItemNodeCd !== nodeData?.itemNodeCd || el.likeCustomerNodeCd !== nodeData?.customerNodeCd);
        if (action === "avg") {
          for (const item of deleteItem) {
            item.weightRate = 100 / deleteItem.length;
          }
        }
        setSelectedItems(deleteItem)
        setGridData(gridData.filter(el => el.itemCd !== nodeData?.itemNodeCd || el.customerCd !== nodeData?.customerNodeCd))
      }
    }
    return (
      <Box sx={{ mt: 0.3}} >
      <input
        type="checkbox"
        defaultChecked={params.value}
        disabled={selectedNpis.map(item => item.itemCd)?.includes(params.node.data?.itemNodeCd) && params.node.data?.customerNodeCd === npiCustomer?.customerCd}
        onClick={e => checkActive(e)}
      />
    </Box>
    )
  };

  const CustomBooleanIcon = activeProps => {
    const { displayName, selectAll: select, setSelectAll: setSelect } = activeProps;
    const allCheckActive = e => {
      const allActive = e?.target?.checked;
      setSelect(allActive);
    };

    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Box>
          <input
            type="checkbox"
            defaultChecked={select}
            onClick={e => allCheckActive(e)}
          />
        </Box>
        <Box className="customHeaderLabel">{displayName}</Box>
      </Box>
    );
  };

  /*
   * 판매개시일이 조회 구간 안에 있으면 그 이전 버킷은 계산 대상이 아니다.
   * 버킷 라벨(formatHeaders)과 실제 일자(originHeaders)는 같은 순서라 index 로 짝지어 판단한다.
   */
  const getCalcTargetHeaders = (formatHeaders, originHeaders, npi) => {
    const readyToSellinDt = npi?.readyToSellinDt;
    // 판매개시일 제한은 계획반영 전용
    if (!isPlanApply || !readyToSellinDt || isEmpty(originHeaders)) {
      return formatHeaders;
    }

    const readyDate = new ZDate(readyToSellinDt);
    return formatHeaders.filter((header, index) => {
      const baseDate = originHeaders[index];
      if (!baseDate) {
        return true;
      }
      /*
       * 버킷의 마지막 일자 기준으로 판단해 판매개시일이 버킷 중간에 걸리면 그 버킷은 포함한다.
       * 다음 버킷 시작일 -1일 = 이 버킷의 종료일 (마지막 버킷은 조회 종료일).
       */
      const nextBaseDate = originHeaders[index + 1];
      const bucketEnd = nextBaseDate ? subDays(new ZDate(nextBaseDate), 1) : new ZDate(endDate);
      return bucketEnd >= readyDate;
    });
  }

  /*
   * 맵핑(구)제품의 판매 단종일 — 우측 그리드에서 받은 값(z_customer_item 값이 없으면 z_item 값)을 쓴다.
   * 단종일 이후 버킷에 값이 남아 있어도 참조하지 않고 0 으로 본다.
   */
  const getLikeEndOfSalesDt = (like) => {
    if (like?.likeEndOfSalesDt) {
      return like.likeEndOfSalesDt;
    }

    // profile 에 값이 없는 경우(우측 그리드에서 방금 추가한 행 등)는 우측 그리드 데이터에서 찾는다
    const likeCombo = customerItems.find(item =>
      isEqual(item.customerNodeCd, like?.likeCustomerNodeCd) && isEqual(item.itemNodeCd, like?.likeItemNodeCd)
    );
    return likeCombo?.endOfSalesDt ?? null;
  }

  /* 해당 버킷이 맵핑제품의 판매 단종일 이후인지 — 버킷 시작일이 단종일보다 뒤면 단종 이후로 본다 */
  const isAfterEndOfSales = (baseDate, endOfSalesDt) => {
    if (!endOfSalesDt || !baseDate) {
      return false;
    }
    return new ZDate(baseDate) > new ZDate(endOfSalesDt);
  }

  const calculateNpiValue = (act, npiGridData, formatHeaders, originHeaders, npi) => {
    const itemQtySum = {};
    const targetHeaders = getCalcTargetHeaders(formatHeaders, originHeaders, npi);
    const targetHeaderSet = new Set(targetHeaders);
    // 판매개시일 이전 버킷은 계산 대상이 아니고 계획도 0 으로 남겨야 한다
    for (const header of formatHeaders ?? []) {
      if (!targetHeaderSet.has(header)) {
        itemQtySum[header] = 0;
      }
    }

    for (const row of selectedItems) {
      for (const grid of npiGridData) {
        if (row.likeItemNodeCd === grid.itemCd && row.likeCustomerNodeCd === grid.customerCd) {
          for (const header of targetHeaders) {
            if (grid[header]) {
              if (!itemQtySum[header]) {
                itemQtySum[header] = 0;
              }
              if (act === actions[0]) {
                itemQtySum[header] += Math.floor(grid[header] * (Number(row.weightRate)*0.01))
              } else {
                itemQtySum[header] += Math.floor(grid[header] / selectedItems.length)
              }
            } else {
              if (!itemQtySum[header]) {
                itemQtySum[header] = 0;
              } else {
                itemQtySum[header] += 0;
              }
            }
          }
        }
      }
    }
    itemQtySum.modify = true;
    return itemQtySum;
  }

  const selectedItemsColumnDefs = [
    { headerName: isCustomer ? "customerCd" : "customerHrchyCd", field: "likeCustomerNodeCd", editable: false, width: 150 },
    { headerName: isCustomer ? "customerName" : "customerHrchyName", field: "likeCustomerNodeName", editable: false, width: 150 },
    { headerName: isItem ? "itemCd" : "itemHrchyCd", field: "likeItemNodeCd", editable: false, width: 150 },
    { headerName: isItem ? "itemName" : "itemHrchyNm", field: "likeItemNodeName", editable: false, width: 150 },
    { headerName: `${t("weight")} (%)`, field: "weightRate",
      editable: action === actions[0],
      cellEditor: NumericEditor,
      cellStyle: data => dataCellStyle(data),
      type: ["numberColumn", "rightAligned"], width: 130 },
  ];

  const npiProfileColumnDefs = [
    { headerName: "npiCode", field: "itemNodeCd", editable: false, width: 150, cellClass: 'stringType' },
    { headerName: "npi", field: "itemNodeName", editable: false, width: 150 },
    { headerName: isCustomer ? "customerCd" : "customerHrchyCd", field: "customerNodeCd", editable: false, width: 150, cellClass: 'stringType' },
    { headerName: isCustomer ? "customerName" : "customerHrchyName", field: "customerNodeName", editable: false, width: 150 },
    { headerName: isItem ? "likeItemCd" : "likeItemHrchyCd", field: "likeItemNodeCd", editable: false, width: 150, cellClass: 'stringType' },
    { headerName: isItem ? "likeItemName" : "likeItemHrchyName", field: "likeItemNodeName", editable: false, width: 150 },
    { headerName: isCustomer ? "likeCustomerCd" : "likeCustomerHrchyCd", field: "likeCustomerNodeCd", editable: false, width: 150, cellClass: 'stringType' },
    { headerName: isCustomer ? "likeCustomerName" : "likeCustomerHrchyName", field: "likeCustomerNodeName", editable: false, width: 150 },
    { headerName: "computeMethod", field: "actionCd", editable: false, width: 150 },
    { headerName: `${t("weight")} (%)`, field: "weightRate",
      editable: action === actions[0],
      cellEditor: NumericEditor,
      cellStyle: data => dataCellStyle(data),
      type: ["numberColumn", "rightAligned"], width: 130 },
  ]

  const likeProfilesColumnDefs = [
    {
      headerName: "",
      field: "useFlg",
      maxWidth: 45,
      width: 45,
      editable: false,
      cellRenderer: checkFormatter,
      type: ["booleanColumn"],
      headerComponent: CustomBooleanIcon,
      headerComponentParams: {
        enableMenu: false,
        setSelectAll,
        selectAll,
      },
    },
    {
      headerName: isCustomer ? "customerCd" : "customerHrchyCd", field: "customerNodeCd", width: 150, editable: false, filterType: "string"
    },
    { headerName: isCustomer ? "customerName" : "customerHrchyName",  field: "customerNodeName", width: 150, editable: false, filterType: "string" },
    {
      headerName: isItem ? "itemCd" : "itemHrchyCd", field: "itemNodeCd", width: 150, editable: false, filterType: "string"
    },
    { headerName: isItem ? "itemName" : "itemHrchyNm", field: "itemNodeName", width: 150, editable: false, filterType: "string" },
    ...lifecycleDtColDefs,
    ...invClassCdDefs,
    ...hrchyColDefs,
    ...attrColDefs,
    {
      headerName: "initialFlg",
      field: "initialFlg",
      hide: true,
      suppressColumnsToolPanel: true,
      editable: false,
      maxWidth: 45,
      width: 45,
      type: ["booleanColumn"],
    },
  ];

  /* 번역 전 구성(측정 키 목록)만 담는다. t() 는 chartSeries useMemo(렌더 시점)에서 평가한다. */
  const setChartSeriesData = (data) => {
    const keys = [];
    if (Object.keys(data[0]).length > 0) {
      for (const key of Object.keys(data[0])) {
        if (key === "yyyymmdd") {
          continue;
        }
        keys.push(key);
      }
    }
    setChartSeriesKeys(keys)
  }

  const onGridReadySelectedItems = params => {
    setSelectedItemsGridApi(params.api);
    DataState.initialize(params.api);
  };

  const handleImportSelectedItemsFile = (event) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    const custCdHeader = isCustomer ? "customerCd" : "customerHrchyCd";
    const itemCdHeader = isItem ? "itemCd" : "itemHrchyCd";
    const weightHeader = `${t("weight")} (%)`;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const workbook = XLSX.read(evt.target.result, { type: "binary" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const importedRows = XLSX.utils.sheet_to_json(sheet);

      setSelectedItems(prev => prev.map(row => {
        const matched = importedRows.find(r => String(r[custCdHeader]) === String(row.likeCustomerNodeCd)
          && String(r[itemCdHeader]) === String(row.likeItemNodeCd));
        const weight = matched?.[weightHeader];
        return weight === undefined || weight === null || weight === "" ? row : { ...row, weightRate: Number(weight) };
      }));
    };
    reader.readAsBinaryString(file);
    event.target.value = "";
  };
  const onGridReadyNpiItems = params => {
    setNpiItemsGridApi(params.api);
    DataState.initialize(params.api);
  };
  const onGridReadyItems = params => {
    // setItemsGridApi(params.api);
    DataState.initialize(params.api);
  };

  const makeChartData = (pivotDatas, formatHeaderList) => {
    const datas = [];
    if (pivotDatas && formatHeaderList?.gridHeaders) {
      for (const element of formatHeaderList.gridHeaders) {
        const header = element;
        const row = {
          "yyyymmdd": header,
        };
        for (const result of pivotDatas) {
          const key = `${result.customer}-${result.item}`;
          row[key] = !isUndefined(result[header]) ? result[header] : 0;
        }

        if (size(row) > 0) {
          datas.push(row);
        }
      }
    }
    return datas;
  };

  // obj response dat는 company에서 설정한 헤더 데이터.
  const setGridInfo = obj => {
    const colDefs = [];
    const hrchyColDefs = [];
    const invClassCdColDefs = [];

    if (isItem) {
      invClassCdColDefs.push({ headerName: "invClassCd", field: "invClassCd", width: 100, editable: false, filterType: "string", cellRenderer: AbcXyzChip });
      hrchyColDefs.push({ headerName: "itemHrchyNm", field: "itemHrchyName", filterType: "string"});
      // parent의 Column 속성을 다르게 주기위한 attrLoop 변수. (hrchy parent 속성이 필요하다면 hrchyLoop 변수 추가)
      // Target object는 attr column을 따로 지정한 경우가 있는 경우를 체크하는 것. (obj: item, customer)
      const targetObject = obj.item;
      colDefs.push(...attrDef(targetObject, itemAttrFields, itemAttrColsCollapsed, true));
    }
    if (isCustomer) {
      hrchyColDefs.push({ headerName: "customerHrchyName", field: "custHrchyName", filterType: "string" });

      const targetCustomerObject = obj.customer;
      colDefs.push(...attrDef(targetCustomerObject, custAttrFields, custAttrColsCollapsed, false));
    }

    if (setAttrColDefs) {
      setAttrColDefs(colDefs);
    }

    if (setHrchyColDefs) {
      setHrchyColDefs(hrchyColDefs)
    }
    if (setInvClassCdDefs) {
      setInvClassCdDefs(invClassCdColDefs)
    }
  }

  const initialPlanConfig = () => {
    const objVal = { custLevelCd: customer.cd, itemLevelCd: item.cd, itemLevelId: item.id, custLevelId: customer.id }
    const param = {
      moduleCd: DP,
      activeFlg: true,
    };
    let config = {};
    let level = {};
    planConfigService
      .getAll(param)
      .then(response => {
        if (response.data) {
          const responseData = response.data.results;
          for (const result of responseData) {
            if (isEqual(result.configCd, BUCKET_UNIT)) {
              setBucketUnit(result.strVal);
            } else if (isEqual(result.configCd, LEVEL_CONFIG)) {
              for (const key of Object.keys(result?.objVal)) {
                const value = result.objVal[key];
                if (key === "itemLevelId") {
                  result.objVal.itemLevelCd = itemLevelCds?.find(level => level.id === value)?.cd;
                } else if (key === "custLevelId") {
                  result.objVal.custLevelCd = custLevelCds?.find(level => level.id === value)?.cd;
                }
              }
              config = {...result};
              level = result;
            }
          }
          if (isEmpty(level)) {
            config = {configCd: LEVEL_CONFIG, objVal, moduleCd: DP, activeFlg: true};
          }
        } else {
          config = {configCd: LEVEL_CONFIG, objVal, moduleCd: DP, activeFlg: true};
        }
        setLevelConfig(config);
        const itemCd = config?.objVal?.itemLevelId ? itemLevelCds.find(level => level.id === config.objVal?.itemLevelId) : item;
        const custCd = config?.objVal?.custLevelId ? custLevelCds.find(level => level.id === config.objVal?.custLevelId) : customer;

        setModalItemLevelCd(itemCd)
        setModalCustLevelCd(custCd)
      })
      .catch(e => {
        debug(e);
        config = {configCd: LEVEL_CONFIG, objVal, moduleCd: DP, activeFlg: true};
        setModalItemLevelCd(item)
        setModalCustLevelCd(customer)
        setLevelConfig(config)
      })
  };

  const retrieveHrchyConfig = () => {
    if (reduxViewState?.custLevelCds && reduxViewState?.itemLevelCds) {
      const itemCd = reduxViewState ? reduxViewState?.itemLevelCd : item;
      const custCd = reduxViewState ? reduxViewState?.custLevelCd : customer;
      setModalItemLevelCd(itemCd)
      setModalCustLevelCd(custCd)
      return;
    }
    hrchyConfigService.getAll({ searchFilters: { hrchyNum: codeUtil.hrchyNum.HRCHY } })
      .then(response => {
        const itemConfig = [];
        const custConfig = [];
        if (response.data?.results) {
          const result = response.data.results;
          result.sort((a, b) => {
            if (a.seqNum < b.seqNum) {
              return -1;
            }
            if (a.seqNum > b.seqNum) {
              return 1;
            }
            return 0;
          });
          for (const row of result) {
            if (row.itemHrchyFlg) {
              itemConfig.push(row);
            } else {
              custConfig.push(row);
            }
          }
          const itemLevel = [...itemConfig, item];
          const custLevel = [...custConfig, customer]

          setItemLevelCds(itemLevel);
          setCustLevelCds(custLevel);

          if (reduxViewState) {
            reduxViewState.custLevelCds = custLevel
            reduxViewState.itemLevelCds = itemLevel;
            reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...reduxViewState }));
          }
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  const retrieveNpiProfile = (selectedNpis, customerId, reload=false) => {
    return new Promise((resolve, reject) => {
      const itemIds = selectedNpis.map(item => item.id);
      npiHistoryService.getNpiProfile({itemIds, customerId, itemLevelCd: effectiveLevel?.itemLevelCd, custLevelCd: effectiveLevel?.custLevelCd, applyType })
        .then((response) => {
          if (response?.data && response?.data?.length > 0) {
            const weightRows = {};
            const origin = {}
            const keyItemNodeIds = {};
            let hasWeightConflict = false;
            for (const row of response?.data) {
              const key = row.likeCustomerNodeCd + row.likeItemNodeCd;
              if (weightRows[key] && (weightRows[key].weightRate !== row.weightRate
                || weightRows[key].likeCustomerNodeId !== row.likeCustomerNodeId
                || weightRows[key].likeItemNodeId !== row.likeItemNodeId)) {
                hasWeightConflict = true;
                break;
              }
              const params = {
                customerNodeId: customerId ?? npiCustomer.id,
                customerNodeCd: npiCustomer.customerCd,
                customerNodeName: npiCustomer.name,
                likeItemNodeId: row.likeItemNodeId,
                likeItemNodeCd: row.likeItemNodeCd,
                likeItemNodeName: row.likeItemNodeName,
                likeCustomerNodeId: row.likeCustomerNodeId,
                likeCustomerNodeCd: row.likeCustomerNodeCd,
                likeCustomerNodeName: row.likeCustomerNodeName,
                actionCd: row.actionCd,
                weightRate: row.weightRate,
                applyType: row.applyType,
                activeFlg: row.activeFlg,
                verNum: row.verNum
              }
              origin[key] = row.weightRate;
              weightRows[key] = params;

              if (!keyItemNodeIds[key]) {
                keyItemNodeIds[key] = new Set();
              }
              keyItemNodeIds[key].add(row.itemNodeId);
            }
            if (!hasWeightConflict && Object.values(keyItemNodeIds).some(itemNodeIdSet => itemNodeIdSet.size !== itemIds.length)) {
              hasWeightConflict = true;
            }
            if (hasWeightConflict) {
              setSelectedItems([]);
              setOriginNpiProfile({});
            } else {
              const weights = Object.values(weightRows)
              if (reload) {
                setAction(reduxViewState?.action ?? actions[0])
              } else {
                setAction(weights[0]?.actionCd ?? actions[0])
              }
              setSelectedItems([...weights]);
              setOriginNpiProfile(origin);
            }
          } else {
            setSelectedItems([]);
            setOriginNpiProfile({});
            setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
          }
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.networkError, content: t("MSG_NetworkError") });
          debug(e);
          reject(e);
        })
        .then(() => {
          DataState.initialize(selectedItemsGridApi);
          setCheckBoxReload(true);
          resolve();  // ← Promise 완료
        });
    });
  }

  const retrieveNpiHistory = (selectedNpis, customerId, reload = false) => {
    // 이 함수(또는 위임 대상 retrieveNpiPlanValue)가 아직 끝나지 않았으면 새 호출은 그냥 무시한다
    if (npiHistoryLoadingRef.current) {
      return;
    }

    if (isPlanApply) {
      retrieveNpiPlanValue(selectedNpis, customerId, reload);
      return;
    }

    const itemIds = selectedNpis.map(item => item.id);
    /*
     * 플래그는 요청을 던지는 바로 앞에서만 세운다. 리셋이 이 요청의 finally 안에 있으므로,
     * 요청까지 도달하지 못한 return/예외로 플래그가 true 로 박히는 일이 없다.
     */
    npiHistoryLoadingRef.current = true;
    npiHistoryService.getNpiHistory({ itemIds, customerId, itemLevelCd: effectiveLevel?.itemLevelCd, custLevelCd: effectiveLevel?.custLevelCd, startDate, endDate })
      .then((response) => {
        if (response?.data !== null) {
          const result = response.data?.results;
          let headerInfo = null;
          if (response?.data?.header) {
            weekFormatHeader.current = response?.data?.formatHeaders;
            const header = response.data.header;
            headerInfo = setColumnDefs(header, response.data.timeDimension);
            setGridHeader(headerInfo?.colDef)
          }
          if (result != null && result.length > 0) {
            if (reload) {
              npiHistoryList.current = result;
            }
            originHistoryList.current = result;
            const headerObj = headerInfo ?? npiHeaderObj;
            const headerDateMap = new Map();
            (headerObj?.originHeaders ?? []).forEach((originDate, index) => {
              headerDateMap.set(originDate, headerObj?.gridHeaders?.[index]);
            });

            const actualGridData = [];
            for (const npi of selectedNpis) {
              const row = {
                customer: npiCustomer?.name,
                customerCd: npiCustomer?.customerCd,
                itemCd: npi.itemCd,
                item: npi.name
              };
              for (const history of result) {
                if (history.itemCd !== npi.itemCd && history.itemNodeCd !== npi.itemCd) {
                  continue;
                }
                const header = headerDateMap.get(history.baseDate);
                if (header) {
                  row[header] = history.qty;
                }
              }
              actualGridData.push(row);
            }
            sortGridItems(actualGridData);
            setGridData(actualGridData);
          } else {
            const resultGridData = [];
            for (const npi of selectedNpis) {
              const params = {
                customer: npiCustomer?.name,
                customerCd: npiCustomer?.customerCd,
                itemCd: npi.itemCd,
                item: npi.name
              }
              resultGridData.push(params);
            }
            sortGridItems(resultGridData)
            setGridData(resultGridData);
          }
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.networkError, content: t("MSG_NetworkError") });
        debug(e);
      })
      .then(() => {
        DataState.initialize(npiItemsGridApi);
      })
      .finally(() => {
        npiHistoryLoadingRef.current = false;
      });
  }

  /*
   * 계획반영은 z_npi_history 가 아니라 시나리오 버전의 z_dp_cube(MF_QTY)에 저장하므로,
   * 기존 저장값도 같은 곳에서 읽어 온다. 응답(pivot)을 실적반영과 같은 history 형태
   * ({ itemNodeCd, customerNodeCd, baseDate, qty })로 바꿔 담아 이후 로직을 그대로 쓴다.
   */
  const retrieveNpiPlanValue = (npis, customerId, reload = false) => {
    /*
     * 시나리오 버전이 없어 최신 릴리즈 버전으로 대체한 경우에는 신제품×고객 저장값을 읽지 않는다.
     * 조합을 비워 보내면 서버가 헤더(버킷)만 돌려주므로 컬럼 구성은 그대로 된다.
     */
    const comboIdKeys = hasScenarioVersion ? npis.map(npi => customerId + SPLITER + npi.id) : [];
    const params = {
      itemLevelCd: effectiveLevel?.itemLevelCd,
      custLevelCd: effectiveLevel?.custLevelCd,
      startDate,
      endDate,
      applyType,
      comboCustomerItem: { true: comboIdKeys, false: [] }
    };

    planValueLoading.current = true;
    // planValueLoading 과 동일하게 요청 직전에만 세운다 (리셋은 아래 finally)
    npiHistoryLoadingRef.current = true;
    npiHistoryService.getActualSalesByCustomerAndItem(params)
      .then(response => {
        const result = response?.data?.result;
        if (!result) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
          return;
        }

        let headerInfo = null;
        if (result.headers) {
          weekFormatHeader.current = result.formatHeaders;
          headerInfo = setColumnDefs(result.headers, result.timeDimension);
          setGridHeader(headerInfo?.colDef);
        }

        if (result?.results != null && result?.results.length > 0) {
          const histories = [];
          for (const row of result.results ?? []) {
            const itemCd = row.itemCd ?? row.itemHrchyCd;
            const customerCd = row.customerCd ?? row.customerHrchyCd;
            (row.headers ?? []).forEach((baseDate, index) => {
              const qty = row.pivotData?.[index];
              if (qty === null || isUndefined(qty)) {
                return;
              }
              histories.push({ itemNodeCd: itemCd, itemCd, customerNodeCd: customerCd, customerCd, baseDate, qty });
            });
          }
          isFilterChanged.current = true
          if (reload) {
            npiHistoryList.current = histories;
          }
          originHistoryList.current = histories;

          /*
           * 조회한 저장값을 곧바로 그리드에 뿌린다. 이후 retrieveItemsActual 이 like 값과 합쳐 다시
           * 칠해 주지만, 그 호출이 건너뛰어지면 신제품 행이 빈 값으로 남는다.
           * 버킷 컬럼의 key 는 포맷된 헤더 라벨이라 baseDate → 라벨로 바꿔 담는다.
           */
          const headerObj = headerInfo ?? npiHeaderObj;
          const headerDateMap = new Map();
          (headerObj?.originHeaders ?? []).forEach((originDate, index) => {
            headerDateMap.set(originDate, headerObj?.gridHeaders?.[index]);
          });

          const planGridData = [];
          for (const npi of selectedNpis) {
            const row = {
              customer: npiCustomer?.name,
              customerCd: npiCustomer?.customerCd,
              itemCd: npi.itemCd,
              item: npi.name
            };
            for (const history of histories) {
              if (history.itemCd !== npi.itemCd && history.itemNodeCd !== npi.itemCd) {
                continue;
              }
              const header = headerDateMap.get(history.baseDate);
              if (header) {
                row[header] = history.qty;
              }
            }
            planGridData.push(row);
          }
          sortGridItems(planGridData);
          setGridData(planGridData);
        } else {
          const resultGridData = [];
          for (const npi of selectedNpis) {
            const params = {
              customer: npiCustomer?.name,
              customerCd: npiCustomer?.customerCd,
              itemCd: npi.itemCd,
              item: npi.name
            }
            resultGridData.push(params);
          }
          sortGridItems(resultGridData)
          setGridData(resultGridData);
        }
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.networkError, content: t("MSG_NetworkError") });
        debug(e);
      })
      .then(() => {
        DataState.initialize(npiItemsGridApi);
      })
      .finally(() => {
        /*
         * 저장값과 like 값이 같은(무거운) 엔드포인트를 쓰므로 순서를 보장해야 한다.
         * 저장값이 들어온 뒤에 like 값 조회를 한 번만 돌려 저장값이 재계산으로 덮이지 않게 한다.
         */
        planValueLoading.current = false;
        setIsActualLoad(true);
        npiHistoryLoadingRef.current = false;
      });
  }

  const retrieveCustomer = () => {
    const itemIds = selectedNpis.map(item => item.id);
    const params = {
      itemIds: itemIds,
      itemLevelCd: effectiveLevel?.itemLevelCd,
      custLevelCd: effectiveLevel?.custLevelCd
    };
    customerItemService.getCustomerOrHrchy(params)
      .then(response => {
        if (response.data) {
          if (response?.data?.customers) {
            const convertedCustomers = response?.data?.customers.map(cust => ({
              ...cust,
              id: typeof cust.id === 'bigint' ? cust.id.toString() : cust.id
            }));
            setCustomerList(convertedCustomers)
            setNpiCustomer(convertedCustomers[0]);
          }
        }
      })
      .catch(e => {
        debug(e);
      });
  }

  const setCustomerItemGridData = (data) => {
    for (const item of data) {
      if (!isEmpty(item?.itemAttr)) {
        item.itemAttr.forEach((attr, index) => {
          item[itemAttrFields[index]] = attr;
        });
      }
      if (!isEmpty(item?.custAttr)) {
        item.custAttr.forEach((attr, index) => {
          item[custAttrFields[index]] = attr;
        });
      }
    }
    setCustomerItems(data)
    setCheckBoxReload(true);
  }

  const getComboRequestParams = () => {
    const custFilterList = filterValue.customerHrchys?.map(data => data?.hrchyCd);
    const categoryFilterList = filterValue.category?.map(data => data?.hrchyCd);

    const searchFilter = {
      itemLevelCd: effectiveLevel?.itemLevelCd,
      custLevelCd: effectiveLevel?.custLevelCd,
      custFilter: custFilterList,
      itemFilter: categoryFilterList,
      startDate,
      endDate,
      customerFilterList,
      itemFilterList,
      itemFilterType,
      custFilterType,
      invClassCdList: isItem ? invClassCdList : []
    };
    if (comboAdvancedFilters != null && comboAdvancedFilters?.children?.length > 0) {
      searchFilter.advancedFilters = comboAdvancedFilters;
    }
    return searchFilter;
  }

  const retrieveNpiCombo = () => {
    customerItemService.getNpiCombo(getComboRequestParams())
      .then(response => {
        if (response.data) {
          setCustomerItemGridData(response.data.result)
        }
      })
      .catch(e => {
        debug(e);
      })
    if (!isItem) {
      setInvClassCdList([])
    }
    setReload(false)
    if (reduxViewState) {
      reduxViewState.filterValue = filterValue;
      reduxViewState.customerFilterList = customerFilterList;
      reduxViewState.itemFilterList = itemFilterList;
      reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...reduxViewState }));
    }
  }

  const getRequestParams = (itemLevel) => {
    // itemLevelCd: levelConfig ? levelConfig?.objVal.itemLevelCd : itemLevel, advancedFilter:
    const params = {};
    const searchFilter = {};

    searchFilter.itemLevelCd = levelConfig ? effectiveLevel?.itemLevelCd : itemLevel;

    params.searchFilters = searchFilter;
    if (!isEmpty(advancedFilters) && advancedFilters?.children?.length > 0) {
      params.advancedFilters = advancedFilters;
    }
    return params;
  }

  const retrieveItems = (itemLevel = "ITEM") => {
    itemService.getNPIs(getRequestParams(itemLevel))
      .then(response => {
        const npisItem = response.data?.result;
        if (response.data && npisItem.length > 0) {
          // BigInt를 string으로 변환
          const convertedNpisItem = npisItem.map(item => ({
            ...item,
            id: typeof item.id === 'bigint' ? item.id.toString() : item.id
          }));
          setNpiList(convertedNpisItem);
          reduxViewState.npiList = convertedNpisItem;
          reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...reduxViewState }));
        } else {
          setNpiList([]);
          reduxViewState.npiList = [];
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
      })
      .catch(e => {
        debug(e);
      });
    if (reduxViewState) {
      reduxViewState.groupBy = groupBy;
      reduxViewState.itemAttrColsCollapsed = itemAttrColsCollapsed
      reduxViewState.custAttrColsCollapsed = custAttrColsCollapsed
      reduxViewState.itemLevelCd = effectiveLevel?.itemLevelCd;
      reduxViewState.custLevelCd = effectiveLevel?.custLevelCd;
      reduxViewState.levelConfig = levelConfig;
      reduxViewState.applyType = applyType;
      // reduxViewState.selectIHG = selectIHG;
      reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...reduxViewState }));
    }
  };

  const setCSVGridData = (data) => {
    const formatHeaderList = data.header?.formatHeaders;
    const pivot = {};

    if (data.results) {
      for (const row of data.results) {
        const key = `${row.customerNodeCd}_${row.itemNodeCd}`;

        if (!pivot[key]) {
          pivot[key] = {
            customerCd: row.customerNodeCd,
            customer: row.customerNodeName,
            itemCd: row.itemNodeCd,
            item: row.itemNodeName
          };
        }
        const headerNm = formatHeaderList?.[row.baseDate];
        pivot[key][headerNm] = (pivot[key][row.baseDate] || 0) + row.qty;
      }
    }

    return Object.values(pivot);
  }

  const retrieveItemsActual = (act) => {
    if (selectedNpis == null || selectedNpis.length === 0 || isEmpty(npiCustomer)) {
      return;
    }
    let resultGridData = [];
    let gridHeaders = {};

    const npiItems = selectedNpis.map(npi => ({
      customer: npiCustomer?.name,
      customerCd: npiCustomer?.customerCd,
      itemCd: npi.itemCd,
      item: npi.name
    }));

    const params = {
      itemLevelCd: effectiveLevel?.itemLevelCd,
      custLevelCd: effectiveLevel?.custLevelCd,
      startDate,
      endDate,
      // actual: 기준버전 SA_QTY(+sales txn) / plan: 최신 시나리오 버전 MF_QTY
      applyType,
    };

    const hasCache = npiTxnCache.current.length > 0;
    const groupedKeys = { true: new Set(), false: new Set() };
    selectedItems.forEach(item => {
      const key = item.likeCustomerNodeCd + item.likeItemNodeCd;
      const idKey = item.likeCustomerNodeId + SPLITER +item.likeItemNodeId;
      if (!hasCache || !npiTxnCache.current.includes(key)) { groupedKeys[item.activeFlg].add(idKey); }
    });
    params.comboCustomerItem = { true: Array.from(groupedKeys.true), false: Array.from(groupedKeys.false) };

    const existItems = selectedItems
      .filter(item => npiTxnCache.current.includes(item.likeCustomerNodeCd+item.likeItemNodeCd))
      .map(item => item.likeCustomerNodeCd+item.likeItemNodeCd);

    const notExistItems = selectedItems.filter(item => !npiTxnCache.current.includes(item.likeCustomerNodeCd+item.likeItemNodeCd));
    const gridDataRecycle = Object.keys(npiTxnCacheGridData.current)
      .filter(key => {
        const data = npiTxnCacheGridData.current[key];
        return existItems?.includes(data.customerCd+data.itemCd);
      })
      .map(key => {
        return npiTxnCacheGridData.current[key];
      });
    npiHistoryService.getActualSalesByCustomerAndItem(params)
      .then(response => {
        resultGridData = selectedItems ? gridDataRecycle : [];
        let newGridData = [];
        const result = response.data?.result;
        const history = result.results ?? [];
        const header = result.headers;
        weekFormatHeader.current = result.formatHeaders;
        const cache = npiTxnCache.current;
        //key : item.customerCd+item.itemCd
        for (const his of history) {
          const customer = his.customerCd ?? his.customerHrchyCd;
          const item = his.itemCd ?? his.itemHrchyCd;

          cache.push(customer+item)
          npiTxnCacheData.current = { ...npiTxnCacheData.current, [customer+item]: his }
        }
        npiTxnCache.current = cache;
        gridHeaders = setColumnDefs(header, result.timeDimension);
        newGridData = setGridDatas(notExistItems, history, gridHeaders.gridHeaders);

        for (const data of newGridData) {
          npiTxnCacheGridData.current = { ...npiTxnCacheGridData.current, [data.customerCd+data.itemCd]: data }
          resultGridData.push(data);
        }
        npiTxnCacheColDef.current = gridHeaders.colDef;
        setGridHeader(npiTxnCacheColDef.current);
        // sortSelectedItems(resultGridData)
        resultGridData.sort((a, b) => {
          const keyA = `${a.customerCd}_${a.itemCd}`;
          const keyB = `${b.customerCd}_${b.itemCd}`;
          return keyA.localeCompare(keyB);
        });

        if (isEmpty(resultGridData)) {
          resultGridData = [...npiItems];
        } else {
          resultGridData.push(...npiItems);
        }

        if ((isFilterChanged.current && (npiHistoryList.current?.length ?? 0) > 0) || isEmpty(selectedItems)) {
          isFilterChanged.current = false;
          const gridHeadersToUse = Object.keys(gridHeaders).length > 0 ? gridHeaders : npiHeaderObj;
          // ✨ baseDate를 gridHeader 값으로 매핑하는 맵 생성
          const headerDateMap = new Map();
          if (gridHeadersToUse?.originHeaders) {
            gridHeadersToUse.originHeaders.forEach((originDate, index) => {
              headerDateMap.set(originDate, gridHeadersToUse.gridHeaders[index]);
            });
          }
          // ✨ baseDate를 gridHeader 값으로 매핑하는 맵 생성
          for (const npiItem of selectedNpis) {
            const itemQtySum = {};
            // 해당 npiItem과 관련된 history 데이터만 필터링
            const relatedHistories = npiHistoryList.current.filter(history =>
              history.itemCd === npiItem.itemCd || history.itemNodeCd === npiItem.itemCd
            );

            for (const history of relatedHistories) {
              const gridHeaderName = headerDateMap.get(history.baseDate);
              if (gridHeaderName) {
                itemQtySum[gridHeaderName] = history.qty;
              }
            }
            // 해당 npiItem을 resultGridData에서 찾아서 업데이트
            const npiItemIndex = resultGridData.findIndex(row =>
              row.itemCd === npiItem.itemCd &&
              row.customerCd === npiCustomer.customerCd
            );

            if (npiItemIndex !== -1) {
              resultGridData[npiItemIndex] = { ...resultGridData[npiItemIndex], ...itemQtySum };
            }
          }
        }
        else {
          for (const npiItem of selectedItems) {
            const npiItemIndices = resultGridData
              .map((row, index) => (row.customerCd === npiItem.customerNodeCd && selectedNpis?.map(item => item.itemCd)?.includes(row.itemCd)) ? index : -1)
              .filter(index => index !== -1);
            // 해당 npiItem을 resultGridData에서 찾아서 업데이트 (판매개시일 이전 버킷은 계산 제외 — 신제품별로 판단)
            const headerObj = Object.keys(gridHeaders).length > 0 ? gridHeaders : npiHeaderObj;
            for (const npiItemIndex of npiItemIndices) {
              const currentItemCd = resultGridData[npiItemIndex]?.itemCd;
              const targetNpi = selectedNpis.find(npi => isEqual(npi.itemCd, currentItemCd));
              const itemQtySum = calculateNpiValue(act, resultGridData, headerObj?.gridHeaders, headerObj?.originHeaders, targetNpi);
              resultGridData[npiItemIndex] = { ...resultGridData[npiItemIndex], ...itemQtySum };
            }
          }
        }
        setGridData(resultGridData);
        setChartData(makeChartData(resultGridData, Object.keys(gridHeaders).length > 0 ? gridHeaders : npiHeaderObj));
      })
      .catch(e => {
        debug(e);
      })
      .finally(() => {
        setIsActualLoad(false)
      });

    if (reduxViewState) {
      reduxViewState.customerList = customerList;
      reduxViewState.npiList = npiList;
      reduxViewState.startDate = startDate;
      reduxViewState.endDate = endDate;
      reduxViewState.action = act;
      reduxViewState.applyType = applyType;
      reduxViewState.groupBy = groupBy;
      reduxViewState.itemAttrColsCollapsed = itemAttrColsCollapsed
      reduxViewState.custAttrColsCollapsed = custAttrColsCollapsed
      // reduxViewState.selectIHG = selectIHG;
      reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...reduxViewState }));
    }
  };

  /*
   * SCENARIO 상태의 DP 버전 전체 — 조회·저장 기준은 par_id 가 NULL 인 버전(dpScenarioVersion)이고,
   * 이 목록은 "import 하면 이 버전들의 값이 바뀐다"는 안내 메시지에만 쓴다.
   */
  const retrieveDpScenarioVersions = () => {
    planVersionService.getAll(
      {moduleCd: DP, statusCds: [SCENARIO, RELEASED], includeDefault: false})
      .then(response => {
        const results = response?.data?.results ?? [];
        // 요청하지 않은 상태(서버가 끼워 주는 기본 버전 등)는 걸러낸다
        const scenarioVer = results.filter(version => isEqual(version?.statusCd, SCENARIO));
        // SCENARIO 버전이 없으면 최신 릴리즈 버전을 대상으로 쓴다
        const targetVer = !isEmpty(scenarioVer)
          ? scenarioVer
          : sortByRecentVersion(results.filter(version => isEqual(version?.statusCd, RELEASED))).slice(0, 1);

        setHasScenarioVersion(!isEmpty(scenarioVer));

        if (isEmpty(targetVer)) {
          setDpScenarioVersion(null);
          setDpScenarioVersions([]);
          return;
        }

        // 조회·저장 기준은 par_id 가 NULL(최상위) 인 버전. 릴리즈 폴백은 단건이라 그대로 쓴다
        const par = targetVer.filter(version => isEmpty(version?.parent));
        setDpScenarioVersion(!isEmpty(par) ? par[0] : targetVer[0]);
        setDpScenarioVersions(targetVer);
      })
      .catch(e => {
        setHasScenarioVersion(false);
        setDpScenarioVersion(null);
        setDpScenarioVersions([]);
        debug(e);
      });
  }

  /* 릴리즈 일자(없으면 생성 일자) 기준 최신순 */
  const sortByRecentVersion = (versions) => {
    return [...versions].sort((a, b) => {
      const aTs = a?.releaseTs ?? a?.createdTs ?? "";
      const bTs = b?.releaseTs ?? b?.createdTs ?? "";
      return String(bTs).localeCompare(String(aTs));
    });
  }
  const retrieveMfMeasure = () => {
    measureService.getAll({ isBasic: true })
      .then(response => {
        const results = response?.data?.results ?? [];
        const measure = results.find(measure => isEqual(measure.measureCd, MF_QTY.cd));
        if (measure) {
          measure.isMeasureEditable = true;
          setMfMeasure(measure);
        } else {
          setMfMeasure(null);
        }
      })
      .catch(e => {
        debug(e);
      });
  }

  /* 시나리오 버전의 계획 구간(계획 시작일 + 계획 기간) — 계획반영 조회 기간으로 쓴다 */
  const getScenarioVersionPeriod = () => {
    const planConfigObj = dpScenarioVersion?.planConfigObj;
    if (isEmpty(planConfigObj) || isEmpty(planConfigObj.PLAN_START_DATE)) {
      return null;
    }

    const { PLAN_START_DATE, PLANNING_RANGE, BUCKET_UNIT, START_DAY } = planConfigObj;
    return {
      startDate: new ZDate(PLAN_START_DATE),
      endDate: dateUtils.calculateRangeDate(PLAN_START_DATE, BUCKET_UNIT, START_DAY, PLANNING_RANGE, true)
    };
  }

  const retrieveCustomConfig = () => {
    companyService.getCustomConfig()
      .then(response => {
        setGridInfo(response.data);
        setCustomConfig(response.data);
      })
      .catch(e => {
        debug(e);
      });
  }

  /*
   * NPI 그리드의 편집 대상(파란) 셀 = 선택한 신제품 × 선택한 customer 행의 버킷 값.
   * 계획반영에서는 이 값을 시나리오 버전의 MF_QTY 로 z_dp_cube 에 저장하므로 WorkbenchDto 형태로 만든다.
   */
  const buildDpCubeSaveTarget = () => {
    const originHeaders = npiHeaderObj?.originHeaders ?? [];
    const gridHeaders = npiHeaderObj?.gridHeaders ?? [];
    const versions = !isEmpty(dpScenarioVersions) ? dpScenarioVersions : (dpScenarioVersion ? [dpScenarioVersion] : []);
    if (isEmpty(originHeaders) || isEmpty(gridHeaders) || isEmpty(versions) || isEmpty(npiCustomer)) {
      return [];
    }

    // 버전과 무관한 공통 행을 먼저 만든 뒤, 버전별로 versionDto 만 바꿔 복제한다
    const targets = [];
    for (const npi of selectedNpis) {
      const npiRow = gridData.find(row => isEqual(row.itemCd, npi.itemCd) && isEqual(row.customerCd, npiCustomer.customerCd));
      if (!npiRow) {
        continue;
      }

      const edited = {};
      gridHeaders.forEach((header, index) => {
        const value = npiRow[header];
        if (value !== null && !isUndefined(value) && value !== "" && !isNaN(Number(value))) {
          edited[index] = Number(value);
        } else {
          edited[index] = 0;
        }
      });

      if (isEmpty(edited)) {
        continue;
      }

      targets.push({
        measureCd: MF_QTY.cd,
        measure: mfMeasure,
        customerId: npiCustomer.id,
        customerCd: npiCustomer.customerCd,
        customerNm: npiCustomer.name,
        itemId: npi.id,
        itemCd: npi.itemCd,
        itemNm: npi.name,
        custConfigCd: customer.cd,
        itemConfigCd: item.cd,
        headers: originHeaders,
        npi: true,
        edited
      });
    }

    /*
     * 맵핑(구)제품은 단종일 이후 계획이 남아 있으면 안 된다 — 신제품 값 계산에는 그대로 쓰고,
     * 저장할 때 구제품의 단종일 이후 버킷을 0 으로 함께 저장한다.
     */
    for (const like of selectedItems) {
      const endOfSalesDt = getLikeEndOfSalesDt(like);
      if (!endOfSalesDt) {
        continue;
      }

      const edited = {};
      originHeaders.forEach((baseDate, index) => {
        if (isAfterEndOfSales(baseDate, endOfSalesDt)) {
          edited[index] = 0;
        }
      });

      if (isEmpty(edited)) {
        continue;
      }

      targets.push({
        measureCd: MF_QTY.cd,
        measure: mfMeasure,
        customerId: like.likeCustomerNodeId,
        customerCd: like.likeCustomerNodeCd,
        customerNm: like.likeCustomerNodeName,
        itemId: like.likeItemNodeId,
        itemCd: like.likeItemNodeCd,
        itemNm: like.likeItemNodeName,
        custConfigCd: customer.cd,
        itemConfigCd: item.cd,
        headers: originHeaders,
        npi: true,
        edited
      });
    }

    if (isEmpty(targets)) {
      return [];
    }
    return versions.map(version => targets.map(target => ({ ...target, versionDto: version })));
  }

  /* 버전별로 순차 저장 — DP 전파가 무거워 동시에 던지지 않는다 */
  const saveDpCubeData = (dpSaveTargetsByVersion) => {
    dpSaveTargetsByVersion
      .reduce((chain, targets) => chain.then(() => demandValService.upsertScenario(targets)), Promise.resolve())
      .then(() => {
        setSnackInfo({ open: true, severity: saveSuccess, content: t("MSG_SaveSuccess") });
        /*
         * 저장 완료 후 저장값 기준으로 다시 조회한다.
         * retrieveNpiHistory 는 계획반영이면 z_dp_cube 저장값을 먼저 읽고, 그 뒤에 like 값 조회를 한 번 돌린다
         * (planValueLoading 순서 보장) — 방금 저장한 값이 재계산으로 덮이지 않는다.
         */
        isFilterChanged.current = true;
        // ✅ 순차 실행: retrieveNpiProfile 완료 후에 retrieveNpiHistory 실행
        return retrieveNpiProfile(selectedNpis, npiCustomer?.id, true)
          .then(() => retrieveNpiHistory(selectedNpis, npiCustomer?.id, true))
          .catch(e => {
            debug("Error in sequential retrieve:", e);
          });
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: saveFail, content: t("MSG_SaveFailed") });
        debug(e);
      });
  }

  /* 계획반영은 z_npi_history 대신 z_dp_cube(시나리오 버전 MF_QTY)에 저장한다 */
  const saveNpiGridValue = () => {
    if (!isPlanApply) {
      handleSaveHistory();
      return;
    }

    /*
     * npi profile 만 저장하고 계획 버전 데이터는 건드리지 않는 경우
     *  - 확인 팝업에서 체크를 해제했을 때
     *  - 시나리오 버전이 없어 최신 릴리즈 버전으로 대체됐을 때(읽기 전용)
     */
    if (!hasScenarioVersion || !updateVersionData) {
      setSnackInfo({ open: true, severity: saveSuccess, content: t("MSG_SaveSuccess") });
      return;
    }

    //dpSaveTarget의 originValue 필요.
    const dpSaveTarget = buildDpCubeSaveTarget();
    if (isEmpty(dpSaveTarget)) {
      return;
    }
    if (isEmpty(mfMeasure)) {
      setSnackInfo({ open: true, severity: saveFail, content: t("MSG_SaveFailed") });
      debug("MF_QTY measure is not loaded");
      return;
    }
    saveDpCubeData(dpSaveTarget);
  }

  const handleSave = () => {
    if (selectedNpis.length === 0 || Object.keys(npiCustomer).length <= 0) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
      return;
    }

    // npi profile(가중치 행)이 없으면 저장할 게 없다
    if (isEmpty(selectedItems)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
      return;
    }

    if (selectedItems.length > 0) {
      const inputData = []
      for (const npi of selectedNpis) {
        for (const select of selectedItems) {
          const row = {};
          row.itemNodeId = npi.id;
          row.customerNodeId = select.customerNodeId;
          row.likeCustomerNodeId = select.likeCustomerNodeId;
          row.likeCustomerNodeCd = select.likeCustomerNodeCd;
          row.likeCustomerNodeName = select.likeCustomerNodeName;
          row.likeItemNodeId = select.likeItemNodeId;
          row.likeItemNodeCd = select.likeItemNodeCd;
          row.likeItemNodeName = select.likeItemNodeName;
          row.weightRate = select.weightRate;
          row.actionCd = action;
          row.applyType = applyType;
          row.customerHrchyFlg = !isCustomer;
          row.itemHrchyFlg = !isItem;
          row.activeFlg = select.activeFlg ?? true;
          inputData.push(row);
        }
      }
      npiHistoryService.upsertNpiProfile(inputData)
        .then((response) => {
          saveNpiGridValue()
          const weightRows = []
          const origin = {}
          for (const row of response.data) {
            origin[row.likeCustomerNodeCd+row.likeItemNodeCd] = row.weightRate;
          }
          weightRows.push(...response.data)
          setSelectedItems(sortSelectedItems(weightRows));
          setOriginNpiProfile(origin);
          // setSnackInfo({ open: true, severity: saveSuccess, content: t("MSG_SaveSuccess") });
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: saveFail, content: t("MSG_SaveFailed") });
          debug(e);
        })
    } else {
      setSnackInfo({open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData")});
    }
  }

  const handleSaveHistory = () => {
    const saveData = [];
    // selectedNpis의 모든 항목에 대해 처리
    for (const npi of selectedNpis) {
      const npiData = gridData.filter(item =>
        item.itemCd === npi.itemCd &&
        item.customerCd === npiCustomer.customerCd
      );

      if (npiData.length > 0) {
        for (let i = 0; i < npiHeaderObj.gridHeaders.length; i++) {
          const gridValue = npiData[0][npiHeaderObj.gridHeaders[i]];
          // 판매개시일 이전 버킷은 null 이라 저장 대상에서 제외한다 (null >= 0 은 true 가 되므로 명시적으로 걸러낸다)
          if (gridValue !== null && !isUndefined(gridValue) && gridValue >= 0) {
            const params = {
              baseDate: npiHeaderObj.originHeaders[i],
              itemNodeId: npi.id,
              itemNodeCd: npi.itemCd,
              itemNodeName: npi.name,
              customerNodeId: npiCustomer.id,
              customerNodeCd: npiCustomer.customerCd,
              customerNodeName: npiCustomer.name,
              qty: npiData[0][npiHeaderObj.gridHeaders[i]],
              startDate,
              endDate,
              customerHrchyFlg: !isCustomer,
              itemHrchyFlg: !isItem,
            }
            saveData.push(params)
          }
        }
      }
    }
    if (saveData.length > 0) {
      npiHistoryService.upsertNpiHistory(saveData)
        .then((response) => {
          npiHistoryList.current = response.data;
          originHistoryList.current = response.data;
          isFilterChanged.current = true;
          setIsActualLoad(true)
          setSnackInfo({ open: true, severity: saveSuccess, content: t("MSG_SaveSuccess") });
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          debug(e);
        })
    }
  }

  /*
   * 저장 확인 팝업 — 계획반영은 import 확인 팝업과 같은 정보(대상 시나리오 버전)와
   * "해당 버전의 데이터를 변경하시겠습니까?" 체크박스(기본 체크)를 함께 보여 준다.
   * 체크를 해제하면 npi profile 만 저장한다.
   */
  const openSaveConfirm = () => {
    // npi profile 이 없으면 확인 팝업 없이 바로 알린다
    if (selectedNpis.length === 0 || isEmpty(npiCustomer) || isEmpty(selectedItems)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
      return;
    }

    // 시나리오 버전이 없어 최신 릴리즈 버전으로 대체한 경우에는 버전 목록·확인 체크박스를 띄우지 않는다
    if (!isPlanApply || !hasScenarioVersion) {
      setUpdateVersionData(true);
      setDialogInfo({ open: true, title: "saveNpi", content: t("MSG_SaveConfirm") });
      return;
    }

    setUpdateVersionData(true);
    setDialogInfo({
      open: true,
      title: "saveNpi",
      content: (
        <Box>
          {!isEmpty(dpScenarioVersions) ? (
            <Box sx={{ fontSize: FS.TITLE, color: "text.secondary", mb: 1 }}>
              <div>{t("version")}: {dpScenarioVersions.map(version => version.name).join(", ")}</div>
            </Box>
          ) : null}
          <Box>{t("MSG_SaveConfirm")}</Box>
        </Box>
      ),
      checkBox: { checkBox: true, checkBoxMessage: t("MSG_NpiImportPlanVersionConfirm") }
    });
  }

  const handleDialog = actionName => {
    if (actionName === "SAVENPI") {
      handleSave();
    }
  };

  const handleItemChange = (event, newValues) => {
    if (!isEmpty(newValues)) {
      setSelectedNpis(newValues);
      setSelectedItems([]);
    } else {
      setSelectedNpis([]);
    }
  };

  const handleCustomerChange = (event, newValues) => {
    const match = customerList?.find(item => item.name === newValues);
    if (!isUndefined(match)) {
      setNpiCustomer(match);
      setSelectedItems([]);
      npiHistoryList.current = [];  // ✅ 추가: 캐시 초기화
      originHistoryList.current = [];
    }
  };

  const handleChange = (event) => {
    const { type } = event;
    const { target: { value, name }, } = event;
    if (type === "change") {
      setGroupBy(value);
    } else {
      if (name === "action") {
        npiHistoryList.current = [];
        setAction(value);
        retrieveItemsActual(value);
      } else if (name === "applyType") {
        npiHistoryList.current = [];
        setApplyType(value);
      } else if (name === CUST_LEVEL) {
        const custlevel = custLevelCds?.filter(function (el) {
          return el.id === value;
        })[0] ?? customer;
        setModalCustLevelCd(custlevel);
      } else if (name === ITEM_LEVEL) {
        const itemlevel = itemLevelCds.filter(function (el) { return el.id === value })[0] ?? item;
        setModalItemLevelCd(itemlevel);
      }
    }
  };

  const onCellValueChanged = (param) => {
    const newGridItem = [];
    npiHistoryList.current = [];
    param.data.modify = true;
    const selectedNpisSet = new Set(selectedNpis.map(n => n.itemCd));
    if (param.colDef.field === "weightRate") {
      for (const profile of selectedItems) {
        if (profile.likeItemNodeCd === param.data.likeItemNodeCd && profile.likeCustomerNodeCd === param.data.likeCustomerNodeCd) {
          profile.weightRate = param.data.weightRate;
        }
      }
      // const from = gridData?.find(grid => grid.itemCd === param.data.likeItemNodeCd && grid.customerCd === param.data.likeCustomerNodeCd);
      for (let grid of gridData) {
        if (selectedNpisSet.has(grid.itemCd) && grid.customerCd === npiCustomer.customerCd) {
          // 신제품마다 판매개시일이 달라 계산 대상 버킷도 달라진다
          const targetNpi = selectedNpis.find(npi => isEqual(npi.itemCd, grid.itemCd));
          const itemQtySum = calculateNpiValue(action, gridData, npiHeaderObj.gridHeaders, npiHeaderObj.originHeaders, targetNpi);
          grid = { ...grid, ...itemQtySum };
        }
        newGridItem.push(grid);
      }
    } else {
      for (const grid of gridData) {
        // param.data.itemCd가 selectedNpis 중에 있는지 확인
        const isChangedNpi = selectedNpis.some(npiItem =>
          npiItem.itemCd === param.data.itemCd
        );

        if (isChangedNpi && grid.itemCd === param.data.itemCd && grid.customerCd === param.data.customerCd) {
          for (const item of Object.keys(param.data)) {
            if (item === "item" || item === "itemCd" || item === "customer" || item === "customerCd") {
              grid[item] = param.data[item];
            } else {
              grid[item] = Number(param.data[item])
            }
          }
        }
        newGridItem.push(grid);
      }
    }
    setGridData(newGridItem);
    setChartData(makeChartData(newGridItem, npiHeaderObj));
  }
  const filterActionCustomer = params => {
    if (params.type === "click") {
      setModelCode(customer.name);
      setFilterModalOpen(true);
    }
  };

  const filterActionItem = params => {
    if (params.type === "click") {
      setModelCode(item.name);
      setFilterModalOpen(true);
    }
  };

  const handelFilterClose = () => {
    setFilterModalOpen(false);
  };

  const handleSettingClose = () => {
    setSettingModalOpen(false);
  };

  const saveConfig = () => {
    const params = [];
    if (size(levelConfig) > 0) {
      levelConfig.objVal = {
        custLevelId: modalCustLevelCd.id,
        itemLevelId: modalItemLevelCd.id
      }
      params.push({...levelConfig});
    } else {
      params.push({
        moduleCd: "DP",
        configCd: LEVEL_CONFIG,
        activeFlg: true,
        objVal: {
          custLevelId: modalCustLevelCd.id,
          itemLevelId: modalItemLevelCd.id
        }
      });
    }

    planConfigService.upsert(params)
      .then((response) => {
        if (response?.data) {
          const result = response.data[0];
          if (result) {
            result.objVal.custLevelCd = modalCustLevelCd.cd
            result.objVal.itemLevelCd = modalItemLevelCd.cd
          }
          setLevelConfig({...result})
          if (reduxViewState) {
            reduxViewState.levelConfig = {...result};
            reduxViewState.custLevelCd = modalCustLevelCd;
            reduxViewState.itemLevelCd = modalItemLevelCd;
            reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...reduxViewState }));
          }
        }
        setSnackInfo({ open: true, severity: saveSuccess, content: t("MSG_SaveSuccess") });
        setReload(true);
        handleReset();
        handleSettingClose();
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: saveFail, content: t("MSG_SaveFailed") });
        debug(e);
      })
      .then(() => {
        handleReset();
        handleSettingClose();
      })
    return true;
  }

  const filterCheck = (customerHrchy, itemHrchy) =>{
    let filterFlg = false;
    if (isEmpty(filterValue) && reload) {
      filterFlg = true;
    }
    if (!isEmpty(customerHrchy) && (reload || !isEqual(custCustomValue, customerHrchy))) {
      filterFlg = true;
    }
    if (!isEmpty(itemHrchy) && (reload || !isEqual(itemCustomValue, itemHrchy))) {
      filterFlg = true;
    }
    return filterFlg;
  }

  const compareHrchys = (org, pre) => {
    const orgList = cloneDeep(org)?.map((hrchy) => hrchy.hrchyCd);
    const preList = cloneDeep(pre)?.map((hrchy) => hrchy.hrchyCd);
    return isEqual(orgList, preList);
  }

  const collapseFilter = () => {
    setOpenFilter(!openFilter);
  };

  const collapseComboFilter = () => {
    setOpenComboFilter(!openComboFilter);
  };

  const handleAdvancedFilterApply = (filters) => {
    //clean
    handleReset()
    setSelectedNpis([]);
    setNpiCustomer({});

    setAdvancedFilters(filters);
    setAdvancedFilterColor(!isEmpty(filters) ? muicolor.INFO : muicolor.DEFAULT);
    if (reduxViewState) {
      reduxViewState.advancedFilters = filters;
      reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...reduxViewState }));
    }
  };

  const handleComboAdvancedFilterApply = (filters) => {
    setComboAdvancedFilters(filters);
    setComboAdvancedFilterColor(!isEmpty(filters) ? muicolor.INFO : muicolor.DEFAULT);
    if (reduxViewState) {
      reduxViewState.comboAdvancedFilters = filters;
      reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...reduxViewState }));
    }
  };

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    if (!isEmpty(levelConfig) && !isEmpty(advancedFilters)) {
      retrieveItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters])

  useEffect(() => {
    if (!isEmpty(comboAdvancedFilters) && comboAdvancedFilters?.children?.length > 0) {
      setComboAdvancedFilterColor("info");
    } else {
      setComboAdvancedFilterColor("default");
    }
    if (!isEmpty(levelConfig) && !isEmpty(comboAdvancedFilters)) {
      retrieveNpiCombo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comboAdvancedFilters])

  useEffect(() => {
    //level이 변경될 때.
    if (!levelConfigMounted.current) {
      levelConfigMounted.current = true;
      return;
    }
    if (!isEmpty(levelConfig)) {
      handleReset();
      setSelectedNpis([]);
      setNpiCustomer({});

      retrieveNpiCombo();
      retrieveCustomConfig();
      retrieveItems();

      if (levelConfig?.objVal.itemLevelCd === item.cd) {
        setDisabledInvClass(false);
      } else {
        setDisabledInvClass(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levelConfig])

  useEffect(() => {
    if (!applyTypeMounted.current) {
      applyTypeMounted.current = true;
      return;
    }
    handleReset();
    setSelectedNpis([]);
    setNpiCustomer({});
    setDisabledInvClass(!isItem);
    
    const planPeriod = isPlanApply ? getScenarioVersionPeriod() : null;
    let startValue;
    let endValue;
    if (planPeriod) {
      startValue = planPeriod.startDate;
      endValue = planPeriod.endDate;
    } else {
      const nextStart = isPlanApply ? PLAN_FALLBACK_START_DATE : START_DATE;
      const nextEnd = isPlanApply ? PLAN_FALLBACK_END_DATE : CURRENT_DATE;
      startValue = isWeekBucket(bucketUnit) ? nextStart : dateUtils.startOfMonthDate(nextStart);
      endValue = isWeekBucket(bucketUnit) ? nextEnd : dateUtils.endOfMonthDate(nextEnd);
    }
    setStartDate(startValue);
    setEndDate(endValue);

    retrieveNpiCombo();
    retrieveItems();

    if (reduxViewState) {
      reduxViewState.applyType = applyType;
      reduxViewState.startDate = startValue;
      reduxViewState.endDate = endValue;
      reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...reduxViewState }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyType])

  // SCENARIO 버전이 없으면 계획반영을 쓸 수 없다 — redux 로 복원된 값이 계획반영이면 실적반영으로 되돌린다
  useEffect(() => {
    if (!dpScenarioVersion && isPlanApply) {
      setApplyType(applyTypes[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dpScenarioVersion])

  useEffect(() => {
    let filterFlg = false;
    const customerHrchy = filterValue?.customerHrchys;
    const itemHrchy = filterValue?.category;
    filterFlg = filterCheck(customerHrchy, itemHrchy);
    if (size(customerHrchy) > 0) {
      if (reduxViewState?.custLevelCd && !isEqual(reduxViewState.custLevelCd, customerHrchy[0]?.hrchyLv)) {
        filterFlg = true;
      }
      setCustomerFilterList({});
      setCustomerFilterColor(muicolor.DEFAULT);
    } else {
      setTreeload(true);
      if (reduxViewState?.custLevelCd && !isEqual(reduxViewState.custLevelCd, customer.cd)) {
        filterFlg = true;
      }
    }
    if (size(itemHrchy) > 0) {
      if (reduxViewState?.itemLevelCd && !isEqual(reduxViewState.itemLevelCd, itemHrchy[0]?.hrchyLv)) {
        filterFlg = true;
        setInvClassCdList([]);
        setDisabledInvClass(true);
      }
      setItemFilterList({});
      setItemFilterColor(muicolor.DEFAULT);
    } else {
      setItemTreeLoad(true);
      if (reduxViewState?.itemLevelCd && !isEqual(reduxViewState.itemLevelCd, item.cd)) {
        filterFlg = true;
      }
    }
    if (!compareHrchys(itemHrchy, itemCustomValue)) {
      setItemCustomValue(itemHrchy);
    } else if (!compareHrchys(customerHrchy, custCustomValue)) {
      setCustCustomValue(customerHrchy);
    } else {
      filterFlg = false;
    }
    if (reduxViewState && filterFlg) {
      setReload(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue]);

  /*
   * 아이템/고객 필터 모달을 적용하면 filterValue 의 계층 선택은 비워지기만 해서 위 [filterValue] effect 로는
   * 재조회가 걸리지 않는다. 필터 조건이 실제로 바뀐 경우에만 우측 조합 그리드를 다시 조회한다.
   */
  useEffect(() => {
    const filterListKey = JSON.stringify({ itemFilterList, itemFilterType, customerFilterList, custFilterType });
    if (appliedFilterListKey.current === null) {
      appliedFilterListKey.current = filterListKey;
      return;
    }
    if (isEqual(appliedFilterListKey.current, filterListKey)) {
      return;
    }
    appliedFilterListKey.current = filterListKey;

    if (isEmpty(levelConfig)) {
      return;
    }
    retrieveNpiCombo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemFilterList, itemFilterType, customerFilterList, custFilterType]);

  useEffect(() => {
    if ((!isEmpty(invClassCdList) || !isEmpty(reduxViewState?.invClassCdList)) && !isEqual(reduxViewState?.invClassCdList, invClassCdList)) {
      setReload(true);
    }
    if (reduxViewState) {
      reduxViewState.invClassCdList = invClassCdList;
      reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...reduxViewState }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invClassCdList]);

  useEffect(() => {
    if (reload) {
      retrieveNpiCombo();
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload])

  useEffect(() => {
    if (checkBoxReload) {
      const selectedSet = new Set(
        selectedItems?.map(w => `${w.likeItemNodeCd}-${w.likeCustomerNodeCd}`)
      );

      for (const item of customerItems) {
        const key = `${item.itemNodeCd}-${item.customerNodeCd}`;
        const matched = selectedSet?.has(key);

        item.useFlg = matched;
        item.initialFlg = matched;
      }
      setCheckBoxReload(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkBoxReload])

  useEffect(() => {
    if (size(filterData) > 0) {
      if (filterData?.modelCode === customer.name) {
        setCustomerFilterList(filterData.filterList);
        setCustomerFilterColor(size(filterData?.filterList) > 0 ? muicolor.INFO : muicolor.DEFAULT);
        setFilterValue({ ...filterValue, customerHrchys: null, customerCheckSelected: null });
        setCustCustomValue([]);
        setTreeload(true);
        setCustFilterType(filterData.filterType);
      } else {
        setItemFilterList(filterData.filterList);
        setItemFilterColor(size(filterData?.filterList) > 0 ? muicolor.INFO : muicolor.DEFAULT);
        setFilterValue({ ...filterValue, category: null});
        setItemCustomValue([]);
        setItemTreeLoad(true);
        setItemFilterType(filterData.filterType);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterData]);

  useEffect(() => {
    if (chartData?.length > 0) {
      setChartSeriesData(chartData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartData]);

  useEffect(() => {
    const rowData = [];
    if (gridRef?.current?.api) {
      gridRef.current.api.forEachNode(node => {
        if (node.rowIndex !== null) {
          const rowNodeData = node.data;
          if (!Object.keys(rowNodeData).includes("useFlg")) {
            rowNodeData.useFlg = false;
          }
          if (rowNodeData.useFlg === !selectAll) {
            rowNodeData.useFlg = selectAll;
            node.setDataValue("useFlg", selectAll);
            rowData.push(rowNodeData);
          }
        }
      });
      const updatedRow = gridRef.current.api.applyTransaction({ update: rowData }).update;
      const checkedItem = [];
      const uncheckedItem = [];
      for (const row of updatedRow) {
        delete row.rowState;
        if (row.data.useFlg) {
          const parameter = {
            customerNodeId: npiCustomer?.id,
            customerNodeCd: npiCustomer?.customerCd,
            customerNodeName: npiCustomer?.name,
            likeCustomerNodeId: row.data.customerNodeId,
            likeCustomerNodeCd: row.data.customerNodeCd,
            likeCustomerNodeName: row.data.customerNodeName,
            likeItemNodeId: row.data.itemNodeId,
            likeItemNodeCd: row.data.itemNodeCd,
            likeItemNodeName: row.data.itemNodeName,
            actionCd: action,
            weightRate: action === actions[0] ? 100 : null,
            activeFlg: row.data.activeFlg ?? true,
          }
          checkedItem.push(parameter)
        } else {
          uncheckedItem.push(row.data.customerNodeCd+row.data.itemNodeCd);
        }
        let newSelectedItems = [...selectedItems, ...checkedItem];
        newSelectedItems.sort((a, b) => {
          const keyA = `${a.customerNodeCd}_${a.itemNodeCd}`;
          const keyB = `${b.customerNodeCd}_${b.itemNodeCd}`;
          return keyA.localeCompare(keyB);
        });

        let gridDataCopy = cloneDeep(gridData);
        for (const item of uncheckedItem) {
          newSelectedItems = newSelectedItems.filter(el => el.likeCustomerNodeCd+el.likeItemNodeCd !== item);
          gridDataCopy = gridDataCopy.filter(el => el.likeCustomerNodeCd+el.likeItemNodeCd !== item)
        }
        setSelectedItems(newSelectedItems);
        setGridData(gridDataCopy);
      }
      gridRef.current.api.redrawRows({rowNodes: updatedRow});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectAll]);

  useEffect(() => {
    if (!isActualLoad) {
      return;
    }
    if (!planValueLoading.current && (selectedItems.length > 0 || selectedNpis.length > 0)) {
      retrieveItemsActual(action);
    }
    if (isPlanApply) {
      setIsActualLoad(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActualLoad]);

  useEffect(() => {
    //relaod grid
    if (selectedItems?.length === 0 || reduxViewState?.selectedItems?.length === selectedItems.length) {
      setCustomerItems([...customerItems]);
    }
    if (reduxViewState) {
      reduxViewState.selectedItems = selectedItems;
      reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...reduxViewState }));
    }
    if (selectedItems?.length > 0 && isPlanApply) {
      setIsActualLoad(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItems]);

  //selectedItems 변경시 actual load
  useEffect(() => {
    if (!isActualLoad && !isEmpty(customerItems) && !isPlanApply && selectedItems.length > 0) {
      setIsActualLoad(true);
      if (isLoad) {
        //처음 load시 flg 상태값 설정
        if (selectedItems.length > 0) {
          const selectedSet = new Set(
            selectedItems?.map(w => `${w.likeItemNodeCd}-${w.likeCustomerNodeCd}`)
          );

          for (const item of customerItems) {
            const key = `${item.itemNodeCd}-${item.customerNodeCd}`;
            const matched = selectedSet?.has(key);

            item.useFlg = matched;
            item.initialFlg = matched;
          }
          // setCustomerItems([...customerItems])
        }
        setIsLoad(false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItems, customerItems])

  useEffect(() => {
    handleReset();
    if (!isLoad && !isEmpty(selectedNpis) && npiCustomer?.id) {
      isFilterChanged.current = true;
      retrieveNpiProfile(selectedNpis, npiCustomer.id)
        .then(() => retrieveNpiHistory(selectedNpis, npiCustomer?.id,true))
        .catch(e => debug("Error in sequential retrieve:", e));
      setIsActualLoad(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  // 사용자가 기간을 바꾸면 조회와 무관하게 두 키만 저장한다.
  //   탭을 오가도 고른 기간이 유지되고, Agent Chat 이 화면 조건(기간)으로 읽는다.
  //   첫 마운트는 건너뛴다 — 롤링 기본값을 저장하면 탭을 연 채 월이 바뀌어도 옛 기본값이 복원된다.
  const periodMountedRef = useRef(false);
  useEffect(() => {
    if (!periodMountedRef.current) {
      periodMountedRef.current = true;
      return;
    }
    reduxDispatch(reduxUtil.updateViewState({ ...reduxState, startDate, endDate }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  useEffect(() => {
    setIsLoad(true);
    retrieveDpScenarioVersions();
    retrieveMfMeasure();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isLoad) {
      retrieveHrchyConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoad]);

  useEffect(() => {
    // redux에 levelConfig가 남아있는 재방문 케이스: [levelConfig] effect는 값이 그대로면(isEqual)
    // 건너뛰므로, npi combo/actual 데이터가 채워지도록 최초 1회 강제로 조회한다.
    if (!isEmpty(reduxViewState?.levelConfig)) {
      retrieveNpiCombo();
      retrieveCustomConfig();
      retrieveItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isEmpty(npiCustomer)) {
      handleReset();
      isFilterChanged.current = true;
      // 순차 실행으로 변경
      if (isRedux.current) {
        retrieveNpiProfile(selectedNpis, npiCustomer.id, true)
          .then(() => retrieveNpiHistory(selectedNpis, npiCustomer.id, false))
          .catch(e => debug("Error in sequential retrieve:", e));
        isRedux.current = false;
      } else {
        retrieveNpiProfile(selectedNpis, npiCustomer.id)
          .then(() => retrieveNpiHistory(selectedNpis, npiCustomer.id, true))
          .catch(e => debug("Error in sequential retrieve:", e));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [npiCustomer]);

  useEffect(() => {
    if (reduxViewState) {
      reduxViewState.selectedNpis = selectedNpis;
      reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...reduxViewState }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNpis]);

  useEffect(() => {
    if (reduxViewState) {
      reduxViewState.npiCustomer = npiCustomer;
      reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...reduxViewState }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [npiCustomer]);


  useEffect(() => {
    if (action === "avg" && selectedItems.length > 0) {
      for (const item of selectedItems) {
        item.weightRate = Number((100 / selectedItems.length).toFixed(2));
      }
    } else if (action === "sum" && Object.keys(originNpiProfile).length > 0) {
      for (const item of selectedItems) {
        item.weightRate = originNpiProfile[item.itemCd] ?? 0;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action])

  useEffect(() => {
    if (custLevelCds != null && !isEmpty(custLevelCds)) {
      initialPlanConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [custLevelCds])


  useEffect(() => {
    if (!isComboFilterResizing) {
      return;
    }

    const handleMouseMove = (event) => {
      const rect = comboFilterLayoutRef.current?.getBoundingClientRect();
      if (!rect) {
        return;
      }

      const minWidth = 320;
      const maxWidth = Math.max(420, Math.floor(rect.width * 0.7));
      const nextWidth = rect.right - event.clientX;
      const clampedWidth = Math.max(minWidth, Math.min(maxWidth, nextWidth));
      setComboFilterPanelWidth(clampedWidth);
    };

    const handleMouseUp = () => setIsComboFilterResizing(false);

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [isComboFilterResizing]);

// handleItemChange 함수 위에 추가
  const renderTags = (value, getTagProps) => {
    if (value.length === 0) return [];
    return [
      <Box key={0} {...getTagProps({ index: 0 })}
           sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              borderRadius: "4px",
              fontSize: FS.H2}}
      >
        {value[0].name}
        {value.length > 1 && (
          <Tooltip
            title={value.slice(1).map(item => item.name).join(", ")}
            arrow
            placement="top"
          >
            <Box sx={{
              backgroundColor: "#EBEBEB",
              borderRadius: "16px",
              width: "25px",
              height: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: FS.SUB,
              cursor: "pointer",
              flexShrink: 0
            }}>
              +{value.length - 1}
            </Box>
          </Tooltip>
        )}
      </Box>
    ];
  };

  return (
    <>
      <Box id="npi-history-grid" sx={{ display: "flex", width: "100%", height: "calc(100% - 70px)", flexGrow: 1, gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0, height: "100%", display: "flex", flexDirection: "column" }}>
          <Box id="npi-history-filter" sx={{ display: "flex", alignItems: "center", pt: 2, pb: 1, ml: 0, gap: 1.5, width: "100%", boxSizing: "border-box", flexWrap: "wrap" }}>
            <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
              <FormControl size="small" sx={{ width: 240, minWidth: 100 }}>
                {/*auto complete 추가 */}
                <Autocomplete
                  disableCloseOnSelect
                  multiple
                  onBlur={() => {
                    //화면이 꺼졌을 때 처리할 로직
                    if (selectedNpis.length > 0) {
                      handleReset();
                      retrieveCustomer();
                    }
                  }}
                  value={selectedNpis} onChange={handleItemChange}
                  inputValue={inputValue}
                  onInputChange={(event, newInputValue) => {setInputValue(newInputValue);}}
                  id="npi-checkbox-select"
                  options={npiList ?? []}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  sx={{ width: "100%" }}
                  size="small"
                  noOptionsText={t("MSG_NoData")}
                  renderTags={renderTags}  // 추가
                  renderOption={(props, option, { selected }) => (
                    <Box component="li" {...props} sx={{ p: 0, m: 0, minHeight: "auto" }}>
                      <Checkbox icon={<CheckBoxOutlineBlankIcon />}
                                checkedIcon={<CheckBoxIcon />}
                                size="small"
                                style={{ marginRight: 8, width: 18, height: 18 }}
                                checked={selected}
                      />
                      {option.name}
                    </Box>
                  )}
                  renderInput={(params) => (
                  <TextField {...params} label={t("npi")}/>
                  )}/>
              </FormControl>
            </Box>
            <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
              <FormControl size="small" sx={{ width: 200, minWidth: 100 }}>
                {/*auto complete 추가 */}
                <Autocomplete
                  value={npiCustomer?.name ?? ""}
                  onChange={handleCustomerChange}
                  inputValue={inputCustValue}
                  onInputChange={(event, newInputValue) => {
                    setInputCustValue(newInputValue);
                  }}
                  id="controllable-states-demo"
                  options={customerList?.map(el => el.name)}
                  sx={{ width: 200 }}
                  size="small"
                  renderInput={(params) => <TextField {...params} label={t("customer")} />}
                />
              </FormControl>
            </Box>
            <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={t("periodStart")}
                views={isWeekBucket(bucketUnit) ? ["year", "month", "day"] : ["year", "month"]}
                format={isWeekBucket(bucketUnit) ? "yyyy-MM-dd" : "yyyy-MM"}
                maxDate={endDate}
                value={startDate}
                openTo={"month"}
                onAccept={newStartValue => {
                  const start = isWeekBucket(bucketUnit) ? newStartValue : dateUtils.startOfMonthDate(newStartValue);
                  setStartDate(start);
                }}
                slotProps={{ textField: { InputProps: { color: 'primary', size: "small", sx: { mr: 1.5, width: isWeekBucket(bucketUnit) ? 150 : 130 } } } }}
              />
              <DatePicker
                label={t("periodEnd")}
                views={isWeekBucket(bucketUnit) ? ["year", "month", "day"] : ["year", "month"]}
                format={isWeekBucket(bucketUnit) ? "yyyy-MM-dd" : "yyyy-MM"}
                value={endDate}
                minDate={startDate}
                openTo={"month"}
                onAccept={newEndValue => {
                  const end = isWeekBucket(bucketUnit) ? newEndValue : dateUtils.endOfMonthDate(newEndValue);
                  setEndDate(end);
                }}
                slotProps={{ textField: { InputProps: { size: "small", sx: { width: isWeekBucket(bucketUnit) ? 150 : 130 } } } }}
              />
            </LocalizationProvider>
            </Box>
            <FormControl sx={{ width: 100, minWidth: 100 }}>
              <InputLabel id="npi-action-label">{t("computeMethod")}</InputLabel>
              <Select
                labelId="npi-action-label"
                variant="outlined"
                id="npi-action"
                size="small"
                name="action"
                value={action}
                onChange={handleChange}
                input={<OutlinedInput label={t("computeMethod")} />}
              >
                {actions?.map((act) => (
                  <MenuItem
                    key={act}
                    value={act}
                  >
                  {t(act === "sum" ? "sumLabel" : act)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* 반영 기준 — 계획반영은 DP SCENARIO 버전이 있을 때만 노출된다 */}
            <FormControl sx={{ width: 150, minWidth: 120 }}>
              <InputLabel id="npi-apply-basis-label">{t("applyType")}</InputLabel>
              <Select
                labelId="npi-apply-basis-label"
                id="npi-apply-basis"
                variant="outlined"
                size="small"
                name="applyType"
                value={applyType}
                onChange={handleChange}
                input={<OutlinedInput label={t("applyType")} />}
              >
                {availableApplyTypes.map((basis) => (
                  <MenuItem
                    key={basis}
                    value={basis}
                  >
                  {t(basis === "PLAN" ? "planApply" : "actualApply")}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ ml: "auto" }} >
              <ExcelExportButton title={"npiProfile"}
                                 service={npiHistoryService}
                                 importInfoRows={[
                                   { label: t("applyType"), value: t(isPlanApply ? "planApply" : "actualApply") },
                                   ...(isPlanApply && hasScenarioVersion && !isEmpty(dpScenarioVersions)
                                     ? [{ label: t("version"), value: dpScenarioVersions.map(version => version.name).join(", ") }]
                                     : [])
                                 ]}
                                 importCheckbox={isPlanApply
                                   ? [
                                     ...(hasScenarioVersion
                                       ? [{ label: t("MSG_NpiImportPlanVersionConfirm"), checked: updateVersionData, onChange: setUpdateVersionData }]
                                       : []),
                                     { label: t("MSG_NpiImportDeleteConfirm"), checked: truncateBeforeImport, onChange: setTruncateBeforeImport }
                                   ]
                                   : null}
                                 params={{
                                   itemLevelCd: effectiveLevel?.itemLevelCd,
                                   custLevelCd: effectiveLevel?.custLevelCd,
                                   startDate,
                                   endDate,
                                   applyType,
                                   updateVersionData: isPlanApply && hasScenarioVersion && updateVersionData,
                                   truncateBeforeImport: isPlanApply && truncateBeforeImport
                                 }}
                                 columnDefs={GridUtils.getColumnDefs(npiProfileColumnDefs)}
                                 isNpi={true}
              />
              <input ref={selectedItemsFileInputRef} hidden type="file" accept=".xlsx,.xls" onChange={handleImportSelectedItemsFile} />
              <SaveButton
                label="save"
                onClick={() => {
                  selectedItemsGridApi.stopEditing(false);
                  openSaveConfirm();
                }}
              />
              {/* Advanced Filter 버튼 */}
              <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
            </Box>
          </Box>
          <Box sx={{ display: "flex", width: "100%", flex: 1, minHeight: 0, gap: 0.5 }}>
            <Box sx={{ flex: 1, minWidth: 0, height: "100%" }}>
            <SplitterContainer initialSizes={[35, 65]}>
              <Box className="ag-theme-balham grid-npi" sx={{ display: "flex", flexDirection: "column", height: "100%", pb: 1 }}>
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  <AgGridReact
                    {...defaultItemWeightRateGridMemo}
                    onGridReady={onGridReadySelectedItems}
                    rowData={selectedItems}
                    columnDefs={GridUtils.getColumnDefs(selectedItemsColumnDefs)}
                    onCellValueChanged={onCellValueChanged}
                  />
                </Box>
              </Box>
              <Box className="ag-theme-balham grid-npi" sx={{ display: "flex", flexDirection: "column", height: "100%", pt: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "flex-end", flexShrink: 0, mb: 1 }}>
                  <ExcelExportButton title={"npiHistory"}
                                     service={npiHistoryService}
                                     params={{
                                       itemLevelCd: effectiveLevel?.itemLevelCd,
                                       custLevelCd: effectiveLevel?.custLevelCd,
                                       startDate, endDate
                                      }}
                                     columnDefs={GridUtils.getColumnDefs(gridHeader)}
                                     setExcelData={setCSVGridData}
                  />
                </Box>
                <Box sx={{ flex: 1, minHeight: 0 }} >
                  <AgGridReact
                    {...defaultBucketItemGridMemo}
                    rowData={gridData}
                    columnDefs={GridUtils.getColumnDefs(gridHeader)}
                    onGridReady={onGridReadyNpiItems}
                    onCellValueChanged={onCellValueChanged}
                  />
                </Box>
                <Box sx={{ mt: 2, flex: size(chartSeries) > 0 ? 1 : 0, minHeight: 0 }} >
                  {size(chartSeries) > 0 ?
                    <ChartComposed
                      xAxis={["yyyymmdd"]}
                      data={chartData}
                      dataSeries={chartSeries}
                      tooltipAnimation={false}
                      options={{ minHeight: "150px", width: "100%" }}
                    />: null}
                </Box>
              </Box>
            </SplitterContainer>
            </Box>
            {/* Advanced Filter Panel */}
            {openFilter && (
              <>
                <ResizableFilterPanel>
                  <AdvancedFilter
                    columnDefs={advancedFilterColumnDefs}
                    advancedFilters={advancedFilters}
                    onApply={handleAdvancedFilterApply}
                    onClose={() => setOpenFilter(false)}
                  />
                </ResizableFilterPanel>
              </>
            )}
          </Box>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, height: "100%", display: "flex", flexDirection: "column" }}>
          <Box id={"npi-history-filter-area"} sx={{ display: "flex", alignItems: "center", width: "100%", pt: 2, pb: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
                <CustomerHrchyTreeFilter viewName={viewName} onChangeFilter={setFilterValue} defaultValue={defaultValue} customValue={custCustomValue} enableSelectParent unuseClearIcon enableMultiSelect treeload={treeload} styles={{ width: 230 }}/>
                <IconButton
                  aria-describedby={"custHrchyPop"}
                  size="small"
                  sx={{ mr: 1 }}
                  onClick={filterActionCustomer}
                  disabled={!isCustomer}
                  color={customerFilterColor}>
                  <Tooltip title={t("custFilter")}>
                    <FilterAlt />
                  </Tooltip>
                </IconButton>
              </Box>
              <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
                <ItemHrchyTreeFilter viewName={viewName} onChangeFilter={setFilterValue} defaultValue={itemHrchyDefaultValue} customValue={itemCustomValue} enableSelectParent unuseClearIcon enableMultiSelect treeload={itemTreeLoad} styles={{ width: 230 }}/>
                <IconButton
                  aria-describedby={"prodHrchyPop"}
                  size="small"
                  sx={{ mr: 1 }}
                  onClick={filterActionItem}
                  disabled={!isItem}
                  color={itemFilterColor}>
                  <Tooltip title={t("itemFilter")}>
                    <FilterAlt />
                  </Tooltip>
                </IconButton>
              </Box>
              <AbcXyzFilterModal viewName={viewName} invClassCdList={invClassCdList} setInvClassCdList={setInvClassCdList} disabled={disabledInvClass} />
            </Box>
            <Box sx={{ flexGrow: 1 }} />
            {/* 계획반영은 CUSTOMER-ITEM 고정이라 레벨 설정이 의미 없다 */}
            <SettingsButton disabled={isPlanApply} onClick={() => setSettingModalOpen(true)} sx={{ ml: "auto" }}/>
            {/* Advanced Filter 버튼 */}
            <FilterButton label="advancedFilter" advancedFilterColor={comboAdvancedFilterColor} onClick={collapseComboFilter} />
          </Box>
          <Box ref={comboFilterLayoutRef} sx={{ display: "flex", flex: 1, minHeight: 0 }}>
            {/* 좌측 그리드는 숨겨진 차트 박스의 mt(16px) 여백 때문에 패널 바닥보다 위에서 끝난다.
                우측엔 그 여백이 없어 더 내려가므로 16px 를 빼서 좌측 끝점과 맞춘다.
                우측 그리드는 세로 스크롤바가 오른쪽 끝에 있어 ag-grid 자체 오른쪽 테두리가 스크롤바에 묻힌다.
                → 이 박스에 오른쪽 테두리 + 오른쪽 안쪽 여백(pr)만 줘서 스크롤바 바깥에 세로 선을 만들고,
                  ag-grid 자체 오른쪽 테두리만 제거해 이중선을 막는다. (상/좌/하단은 ag-grid 기본 테두리를 그대로 쓴다 —
                  pb/전체 테두리를 주면 우측 콘텐츠 높이가 늘어 패널이 커지면서 하단 정렬이 깨진다.) */}
            <Box sx={{ flex: 1, minWidth: 0, height: "calc(100% - 16px)", borderRight: "1px solid #bdc3c7", pr: 1 }}>
              <Box
                className="ag-theme-balham"
                sx={{ width: "100%", height: "100%", "& .ag-root-wrapper": { borderRight: "none" } }}>
                <AgGridReact
                  ref={gridRef}
                  {...defaultItemGridMemo}
                  rowData={customerItems}
                  columnDefs={GridUtils.getColumnDefs(likeProfilesColumnDefs)}
                  onGridReady={onGridReadyItems}
                  pagination
                  paginationPageSize={40}
                  paginationPageSizeSelector={[20, 40, 50, 100]}
                />
              </Box>
          </Box>
            {openComboFilter && (
              <>
                <Box
                  onMouseDown={() => setIsComboFilterResizing(true)}
                  sx={{
                    width: 6,
                    cursor: 'col-resize',
                    mx: 0.5,
                    borderRadius: 1,
                    backgroundColor: isComboFilterResizing ? 'primary.main' : 'divider',
                    '&:hover': { backgroundColor: 'primary.light' }
                  }}
                />
                <Box sx={{ width: `${comboFilterPanelWidth}px`, minWidth: 320, maxHeight: "calc(100vh - 150px)", overflowY: "auto", border: 1, borderColor: "#cccccc" }}>
                  <AdvancedFilter
                    columnDefs={likeProfilesColumnDefs}
                    advancedFilters={comboAdvancedFilters}
                    onApply={handleComboAdvancedFilterApply}
                    onClose={() => setOpenComboFilter(false)}
                  />
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Box>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        checkBox={dialogInfo.checkBox ?? {}}
        checked={updateVersionData}
        setChecked={setUpdateVersionData}
        onHandler={handleDialog}
      />
      <DPFilterModal
        open={filterModalOpen}
        close={handelFilterClose}
        displayModButtons
        modelCode={modelCode}
        modalResultData={setFilterData}
        filteredList={{ Customer: customerFilterList, Item: itemFilterList }}
        filterTypes={{ Customer: custFilterType, Item: itemFilterType }}
      />
      <BaseConfigModal
        open={settingModalOpen}
        onClose={handleSettingClose}
        title={t("npiLevelSetting")}
        onSave={() => saveConfig()}
        size="lg">
          {/* handleChange 는 event.target.name 으로 축을 가른다 — 세그먼트는 값만 주므로 같은 모양으로 만들어 넘긴다.
              type 을 붙이지 말 것: handleChange 가 type === "change" 를 groupBy 변경으로 해석한다. */}
          <HrchyLevelSegments
            label={t(CUST_LEVEL)}
            levels={custLevelCds}
            value={modalCustLevelCd?.id}
            onChange={id => handleChange({ target: { name: CUST_LEVEL, value: id } })}
          />
          <HrchyLevelSegments
            label={t(ITEM_LEVEL)}
            levels={itemLevelCds}
            value={modalItemLevelCd?.id}
            onChange={id => handleChange({ target: { name: ITEM_LEVEL, value: id } })}
            sx={{ mt: 2 }}
          />
      </BaseConfigModal>
    </>
  );
};

export default withTranslation()(NpiHistory);

